import { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import config from "./config";
import MentionTextarea from "./MentionTextarea";
import { serializeMentions } from "./mentions";
import MemberMenu from "./MemberMenu";
import GroupSwitcher from "./GroupSwitcher";
import ProfileModal from "./ProfileModal";
import ChangePasswordModal from "./ChangePasswordModal";
import AccountSettingsModal from "./AccountSettingsModal";
import AuthModal from "./AuthModal";
import PolicyFooter from "./PolicyFooter";
import BannerPopup from "./BannerPopup";
import applyCommunityFont, { applyCommunityDirection } from "./applyFont";
import InlineBanner from "./InlineBanner";
import EmojiPicker from "./EmojiPicker";
import GifPicker from "./GifPicker";
import NotificationBell from "./NotificationBell";
import PageLoader, { FeedSkeleton, Spinner } from "./PageLoader";
import { readTheme, writeTheme } from "./communityTheme";
import WelcomeFlowers from "./WelcomeFlowers";
import ClosedGroupErrorModal from "./ClosedGroupErrorModal";
import GroupLanding from "./GroupLanding";
import { MembersView, LeaderboardView, RewardsView, EventsView, UpcomingEventBanner, MyPointsCard, useMyPoints, SectionHeader } from "./CommunityViews";
import MapView from "./MapView";
import CoursesView, { GroupMiniBanner } from "./CourseViews";
import { TemplateContext } from "./TemplateContext";

const EMPTY_FORM = { name: "", email: "", phone: "", password: "", otp: "", password_confirmation: "", gdpr_consent: false };

// Shared premium input styling + a themed focus ring (border + soft glow in the
// community colour) reused across the composer / edit / comment forms.
const INPUT_CLS =
  "w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none transition focus:bg-white placeholder:text-gray-400";
const themeFocus = (color) => ({
  onFocus: (e) => {
    e.currentTarget.style.borderColor = color;
    e.currentTarget.style.boxShadow = `0 0 0 3px ${color}26`;
  },
  onBlur: (e) => {
    e.currentTarget.style.borderColor = "";
    e.currentTarget.style.boxShadow = "";
  },
});

const DEFAULT_BANNER =
  "https://img.flexifunnels.com/flexiviral-assets/logo-empty.png";

// Master switch for the mobile floating group-info bubble. Disabled per
// request so it never appears on any page; set to true to restore it.
const SHOW_GROUP_INFO_BUBBLE = false;

/**
 * Floating group-info bubble (mobile only). Replaces the right-rail group card
 * on small screens: a bottom-left FAB showing the group banner that opens a
 * premium bottom-sheet with the group's details + stats.
 */
function GroupInfoBubble({ group, totalMembers, primary }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  if (!group) return null;
  const isPrivate = group.access_type === 2;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Group info"
        className="lg:hidden fixed bottom-5 left-4 z-40 w-14 h-14 rounded-full shadow-xl ring-4 ring-white/60 overflow-hidden flex items-center justify-center text-white active:scale-95 transition"
        style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)` }}
      >
        {group.banner && (
          <>
            <img src={group.banner} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
            <span className="absolute inset-0 bg-black/30" />
          </>
        )}
        <svg className="relative w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {open && (
        <div className="lg:hidden fixed inset-0 z-[9999] flex items-end justify-center bg-gray-900/60 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white w-full rounded-t-3xl shadow-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-36 bg-gray-100">
              <img
                src={group.banner || DEFAULT_BANNER}
                alt={group.name}
                className="w-full h-full object-cover"
                onError={(e) => (e.currentTarget.src = DEFAULT_BANNER)}
              />
              <span className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
              <span className="absolute top-2.5 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-white/70" />
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-gray-700 text-lg leading-none shadow"
              >
                ×
              </button>
              <h3 className="absolute bottom-3 left-4 right-4 text-white text-xl font-bold leading-tight flex items-center gap-2">
                <span className="truncate">{group.name}</span>
                {isPrivate && <span title="Private group">🔒</span>}
              </h3>
            </div>
            <div className="p-5 pb-7">
              <p className="text-sm text-gray-600 leading-relaxed">{group.description || "No description provided."}</p>
              <div className="grid grid-cols-3 gap-3 mt-5">
                {[
                  { v: group.member_count, l: "Members" },
                  { v: totalMembers, l: "Community" },
                  { v: isPrivate ? "Private" : "Open", l: "Access" },
                ].map((s, i) => (
                  <div key={i} className="rounded-2xl bg-gray-50 py-3.5 text-center">
                    <div className="font-bold text-gray-900 text-lg" style={{ color: primary }}>{s.v}</div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-0.5">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Skool-style category pills under the composer. Posts have no topic taxonomy,
// so we filter by post_type (1/unset = discussion, 2 = photo, 3 = poll, 4 = video, 5 = audio).
const FILTER_ICONS = {
  all: "M4 6h16M4 12h16M4 18h16",
  text: "M8 10h8M8 14h5M21 12a9 9 0 11-3.6-7.2L21 4v5h-5",
  image: "M4 16l4.5-4.5a2 2 0 012.8 0L16 16m-2-2l1.5-1.5a2 2 0 012.8 0L20 14M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm5 2a1 1 0 11-2 0 1 1 0 012 0z",
  video: "M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z",
  poll: "M9 17V9m4 8V5m4 12v-6M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z",
  audio: "M19 11a7 7 0 01-14 0M12 18v3m0-3a4 4 0 01-4-4V7a4 4 0 018 0v7a4 4 0 01-4 4z",
};
const FEED_FILTERS = [
  { key: "all", label: "All" },
  { key: "text", label: "Discussions" },
  { key: "image", label: "Photos" },
  { key: "video", label: "Videos" },
  { key: "audio", label: "Audio" },
  { key: "poll", label: "Polls" },
];

const FilterGlyph = ({ k, className = "w-[18px] h-[18px]" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
    <path strokeLinecap="round" strokeLinejoin="round" d={FILTER_ICONS[k]} />
  </svg>
);

// Compact "Filter" button + premium popover (mirrors the community page filter).
// Single-select post-type filter shown as labelled chips with counts.
function FeedFilter({ items, active, onChange, primary }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const cur = items.find((i) => i.key === active) || items[0];
  const isFiltered = active && active !== "all";

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
      >
        <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 12h12M10 20h4" />
        </svg>
        <span>{isFiltered ? cur.label : "Filter"}</span>
        {isFiltered && (
          <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white" style={{ background: primary }}>
            {cur.count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-30 mt-2 w-[270px] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl ring-1 ring-gray-900/5">
            {/* header */}
            <div className="flex items-center gap-2 border-b border-gray-100 bg-gradient-to-b from-gray-50/80 to-white px-4 py-3">
              <svg className="h-4 w-4" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 12h12M10 20h4" />
              </svg>
              <p className="text-sm font-bold text-gray-900">Filter posts</p>
            </div>
            {/* options as chips */}
            <div className="flex flex-wrap gap-2 p-3">
              {items.map((f) => {
                const isActive = f.key === active;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => { onChange(f.key); setOpen(false); }}
                    className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[13px] font-semibold transition-all"
                    style={
                      isActive
                        ? { borderColor: primary, backgroundColor: `${primary}14`, color: primary, boxShadow: `0 1px 6px -2px ${primary}66` }
                        : { borderColor: "#e5e7eb", backgroundColor: "#fff", color: "#4b5563" }
                    }
                  >
                    <FilterGlyph k={f.key} className="w-3.5 h-3.5" />
                    {f.label}
                    <span
                      className="rounded-full px-1.5 text-[10px] font-bold"
                      style={isActive ? { backgroundColor: "#fff", color: primary } : { backgroundColor: "#f3f4f6", color: "#6b7280" }}
                    >
                      {f.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Skool-style category pill bar. Renders "All" + each category as pills on a
// single row; any that don't fit collapse into a "More ▾" dropdown at the end
// (measured against the container width, re-measured on resize). Selecting a
// pill filters the feed by that category.
function CategoryBar({ categories, active, onSelect, primary, className = "" }) {
  const all = [{ community_group_category_id: 0, name: "All", emoji: "" }, ...categories];
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const moreRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(all.length);
  const [moreOpen, setMoreOpen] = useState(false);

  // Measure how many pills fit; the rest go into the More dropdown. Re-runs on
  // resize and whenever the category set changes.
  useLayoutEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      const track = measureRef.current;
      if (!container || !track) return;
      const avail = container.clientWidth;
      const pills = Array.from(track.children);
      const GAP = 8;
      const MORE_W = 92; // reserve room for the "More" button when overflowing
      let used = 0;
      let count = 0;
      for (let i = 0; i < pills.length; i++) {
        const w = pills[i].offsetWidth;
        const hasRest = i < pills.length - 1; // items remain after this one
        const reserve = hasRest ? MORE_W + GAP : 0;
        if (used + w + reserve <= avail) {
          used += w + GAP;
          count++;
        } else break;
      }
      setVisibleCount(Math.max(1, count));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [categories]);

  useEffect(() => {
    const h = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pillStyle = (isActive) =>
    isActive
      ? { borderColor: primary, backgroundColor: `${primary}14`, color: primary, boxShadow: `0 1px 6px -2px ${primary}66` }
      : { borderColor: "#e5e7eb", backgroundColor: "#fff", color: "#4b5563" };

  const Pill = ({ c, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold whitespace-nowrap transition-all"
      style={pillStyle(Number(active) === Number(c.community_group_category_id))}
    >
      {c.emoji && <span aria-hidden="true">{c.emoji}</span>}
      {c.name}
    </button>
  );

  const visible = all.slice(0, visibleCount);
  const overflow = all.slice(visibleCount);
  const activeInOverflow = overflow.some(
    (c) => Number(c.community_group_category_id) === Number(active)
  );

  return (
    <div ref={containerRef} className={`relative flex items-center gap-2 ${className}`}>
      {/* Hidden measuring track — every pill at natural width, out of flow and
          clipped so it never widens the row (the container itself must NOT clip,
          or the More dropdown would be hidden). */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-0 overflow-hidden opacity-0"
      >
        <div ref={measureRef} className="flex w-max items-center gap-2">
          {all.map((c) => (
            <Pill key={`m-${c.community_group_category_id}`} c={c} onClick={() => {}} />
          ))}
        </div>
      </div>

      {visible.map((c) => (
        <Pill
          key={c.community_group_category_id}
          c={c}
          onClick={() => onSelect(c.community_group_category_id)}
        />
      ))}

      {overflow.length > 0 && (
        <div className="relative shrink-0" ref={moreRef}>
          <button
            type="button"
            onClick={() => setMoreOpen((o) => !o)}
            className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm font-semibold whitespace-nowrap transition-all"
            style={pillStyle(activeInOverflow)}
          >
            More
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {moreOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setMoreOpen(false)} />
              <div className="absolute right-0 z-30 mt-2 max-h-72 w-56 overflow-y-auto rounded-2xl border border-gray-100 bg-white p-2 shadow-2xl ring-1 ring-gray-900/5">
                {overflow.map((c) => {
                  const isActive = Number(active) === Number(c.community_group_category_id);
                  return (
                    <button
                      key={c.community_group_category_id}
                      type="button"
                      onClick={() => { onSelect(c.community_group_category_id); setMoreOpen(false); }}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold hover:bg-gray-50"
                      style={isActive ? { color: primary, backgroundColor: `${primary}10` } : { color: "#4b5563" }}
                    >
                      {c.emoji && <span aria-hidden="true">{c.emoji}</span>}
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Icon paths for the composer's post-type buttons (trigger row + modal toolbar).
const COMPOSER_ICONS = {
  text: "M5 5h14M12 5v14M9 19h6",
  photo:
    "M4 16l4.5-4.5a2 2 0 012.8 0L16 16m-2-2l1.5-1.5a2 2 0 012.8 0L20 14M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm5 2a1 1 0 11-2 0 1 1 0 012 0z",
  audio: "M19 11a7 7 0 01-14 0M12 18v3m0-3a4 4 0 01-4-4V7a4 4 0 018 0v7a4 4 0 01-4 4z",
  video:
    "M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z",
  poll: "M9 17V9m4 8V5m4 12v-6M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z",
};

// Distinct accent per post type — gives the composer a premium, colour-coded look.
const COMPOSER_ACCENT = {
  text: "#0ea5e9",  // sky
  photo: "#10b981", // emerald
  audio: "#8b5cf6", // violet
  video: "#ef4444", // red
  poll: "#f59e0b",  // amber
};

// A labelled icon button used by the composer trigger row and the Create Post
// modal toolbar. Each post type carries its own accent colour in a tinted badge.
function ComposerActionButton({ icon, label, active, onClick }) {
  const accent = COMPOSER_ACCENT[icon] || "#0ea5e9";
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-1 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition hover:bg-gray-50 hover:border-gray-300"
      style={
        active
          ? { borderColor: accent, color: accent, background: `${accent}0f` }
          : { borderColor: "#e5e7eb", color: "#4b5563" }
      }
    >
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-transform group-hover:scale-110"
        style={{ backgroundColor: `${accent}1f`, color: accent }}
      >
        <svg className="w-[15px] h-[15px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d={COMPOSER_ICONS[icon]} />
        </svg>
      </span>
      <span>{label}</span>
    </button>
  );
}

// Dashed upload drop-zone shown in the Create Post modal for the active media
// mode. Clicking opens the file picker; a dropped file is passed to onFile.
const DROPZONE_CFG = {
  photo: { label: "Add Photo", hint: "PNG, JPG, JPEG, GIF · up to 5 MB", icon: COMPOSER_ICONS.photo },
  video: { label: "Add Video", hint: "MP4, WEBM, MOV · up to 50 MB", icon: COMPOSER_ICONS.video },
  audio: { label: "Add Audio", hint: "MP3, WAV, M4A, AAC · up to 15 MB", icon: COMPOSER_ICONS.audio },
};
function MediaDropzone({ kind, primary, onOpen, onFile, multiple = false }) {
  const cfg = DROPZONE_CFG[kind];
  return (
    <div
      onClick={onOpen}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const files = e.dataTransfer?.files;
        if (!files || !files.length) return;
        onFile(multiple ? files : files[0]);
      }}
      className="mt-3 cursor-pointer rounded-2xl border-2 border-dashed px-4 py-7 text-center transition hover:opacity-90"
      style={{ borderColor: `${primary}80`, background: `${primary}0a` }}
    >
      <span className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full" style={{ background: `${primary}1a`, color: primary }}>
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6">
          <path strokeLinecap="round" strokeLinejoin="round" d={cfg.icon} />
        </svg>
      </span>
      <p className="text-[15px] font-semibold" style={{ color: primary }}>{cfg.label}</p>
      <p className="mt-1 text-xs text-gray-500">{cfg.hint}</p>
    </div>
  );
}

// Max photos per post / comment / reply.
const MAX_MEDIA_IMAGES = 10;

// Facebook/Instagram-style photo gallery for a feed post: a smart grid of up to
// four tiles (with a "+N" overlay when there are more) that opens a full-screen
// lightbox with prev/next navigation.
function PostGallery({ images, compact = false }) {
  const [open, setOpen] = useState(-1); // index of the photo shown in the lightbox, or -1
  const n = images.length;
  const touchStartX = useRef(null); // swipe tracking for the mobile lightbox
  const goPrev = () => setOpen((i) => (i - 1 + n) % n);
  const goNext = () => setOpen((i) => (i + 1) % n);

  // Keyboard navigation + body-scroll lock while the lightbox is open.
  useEffect(() => {
    if (open < 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(-1);
      else if (e.key === "ArrowRight") setOpen((i) => (i + 1) % n);
      else if (e.key === "ArrowLeft") setOpen((i) => (i - 1 + n) % n);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, n]);

  if (!n) return null;

  const tiles = images.slice(0, 4);
  const extra = n - tiles.length; // photos beyond the 4 visible tiles

  const Tile = ({ i, className = "", overlay = 0 }) => (
    <button
      type="button"
      onClick={() => setOpen(i)}
      className={`relative block h-full w-full overflow-hidden ${className}`}
    >
      <img src={images[i]} alt={`photo ${i + 1}`} className="h-full w-full object-cover" />
      {overlay > 0 && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-2xl font-semibold text-white">
          +{extra}
        </span>
      )}
    </button>
  );

  const gridHeight = compact
    ? (n === 2 ? "h-40 sm:h-52" : "h-52 sm:h-64")
    : (n === 2 ? "h-64 sm:h-80" : "h-72 sm:h-96");
  const singleMax = compact ? "max-h-72" : "max-h-[28rem]";

  return (
    <div className={compact ? "mt-1.5" : "mt-3"}>
      {n === 1 ? (
        <button
          type="button"
          onClick={() => setOpen(0)}
          className="block w-full overflow-hidden rounded-xl border"
        >
          <img src={images[0]} alt="photo" className={`${singleMax} w-full object-cover`} />
        </button>
      ) : (
        <div
          className={`grid gap-1 overflow-hidden rounded-xl border ${gridHeight} ${
            n === 2 ? "grid-cols-2" : "grid-cols-2 grid-rows-2"
          }`}
        >
          {tiles.map((_, i) => (
            <Tile
              key={i}
              i={i}
              className={n === 3 && i === 0 ? "row-span-2" : ""}
              overlay={i === tiles.length - 1 ? extra : 0}
            />
          ))}
        </div>
      )}

      {open >= 0 &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90"
            onClick={() => setOpen(-1)}
          >
            <button
              type="button"
              onClick={() => setOpen(-1)}
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-2xl text-white hover:bg-white/20"
              aria-label="Close"
            >
              ×
            </button>
            {n > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goPrev();
                  }}
                  className="absolute left-2 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-3xl leading-none text-white shadow-lg hover:bg-white/30 sm:left-4"
                  aria-label="Previous photo"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goNext();
                  }}
                  className="absolute right-2 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-3xl leading-none text-white shadow-lg hover:bg-white/30 sm:right-4"
                  aria-label="Next photo"
                >
                  ›
                </button>
              </>
            )}
            <img
              src={images[open]}
              alt={`photo ${open + 1}`}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => {
                touchStartX.current = e.touches[0].clientX;
              }}
              onTouchEnd={(e) => {
                if (touchStartX.current == null) return;
                const dx = e.changedTouches[0].clientX - touchStartX.current;
                touchStartX.current = null;
                if (n > 1 && Math.abs(dx) > 40) {
                  e.stopPropagation();
                  dx < 0 ? goNext() : goPrev();
                }
              }}
              className="max-h-[90vh] max-w-[92vw] touch-pan-y select-none object-contain"
            />
            {n > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-sm text-white">
                {open + 1} / {n}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

// Maps a group nav tab to its per-group menu-hide flag (group.menus.*).
// Tabs not listed here (groups/feed) are always shown.
const TAB_MENU_KEY = {
  courses: "course",
  events: "event",
  members: "member",
  map: "map",
  leaderboard: "leaderboard",
  rewards: "reward",
};

// Admin-only "Pending Posts" entry for the header "More" dropdown (Approve Posts).
const PENDING_ICON_PATH = "M6 3.75h12M6 20.25h12M7.5 3.75v2.379a3 3 0 00.879 2.121L12 12l3.621-3.621a3 3 0 00.879-2.121V3.75M7.5 20.25v-2.379a3 3 0 01.879-2.121L12 12l3.621 3.621a3 3 0 01.879 2.121v2.379";

const NAV_PENDING_ITEM = {
  key: "pendingpost",
  label: "Pending Posts",
  icon: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d={PENDING_ICON_PATH} />
    </svg>
  ),
};

// Maps the current group sub-page to its banner placement key, so popup banners
// honour the same per-page placements as inline banner ads.
const SUBVIEW_PLACEMENT = {
  feed: "group_page",
  members: "member_page",
  map: "map_page",
  leaderboard: "leaderboard",
  rewards: "reward_page",
  events: "event_page",
  about: "about_page",
};

const NAV_ABOUT_ITEM = {
  key: "about",
  label: "About",
  icon: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

/**
 * Compact group-nav dropdown for small/medium screens (replaces the horizontal
 * scrolling tab bar). Shows the active section, opening a premium popover list
 * of all sections (incl. About). Mirrors the desktop tabs' behaviour.
 */
function GroupNavMenu({ navTabs, showAbout = true, showPending = false, subView, onNavigate, setSubView, primary }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const items = [
    ...navTabs,
    ...(showAbout ? [NAV_ABOUT_ITEM] : []),
    ...(showPending ? [NAV_PENDING_ITEM] : []),
  ];
  const current =
    items.find((t) => t.key !== "groups" && t.key === subView) ||
    items.find((t) => t.key === "feed") ||
    items[0];

  const handle = (t) => {
    setOpen(false);
    if (t.key === "groups") onNavigate?.("groups");
    else setSubView(t.key);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 max-w-[42vw] sm:max-w-[40vw] px-2.5 sm:px-3.5 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-800 transition"
      >
        <span className="shrink-0" style={{ color: primary }}>{current.icon}</span>
        <span className="truncate hidden min-[400px]:inline">{current.key === "groups" ? "Community" : current.label}</span>
        <svg className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-[2000] w-60 bg-white rounded-2xl border border-gray-200 shadow-xl ring-1 ring-black/5 p-1.5 max-h-[70vh] overflow-y-auto max-sm:fixed max-sm:left-3 max-sm:right-3 max-sm:top-[68px] max-sm:mt-0 max-sm:w-auto max-sm:translate-x-0">
          {items.map((t) => {
            const isGroups = t.key === "groups";
            const active = !isGroups && subView === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => handle(t)}
                className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-semibold transition"
                style={active ? { background: `${primary}0f`, color: primary } : { color: "#374151" }}
              >
                <span
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={active ? { background: `${primary}1a`, color: primary } : { background: "#f3f4f6", color: "#6b7280" }}
                >
                  {t.icon}
                </span>
                <span className="flex-1 text-left truncate">{isGroups ? "Community" : t.label}</span>
                {active && (
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke={primary} strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * "More ▾" overflow dropdown for the desktop horizontal nav — holds the
 * secondary sections (Leaderboard / Rewards / About) so the primary tabs stay
 * on one row like the reference design.
 */
function MoreNavMenu({ items, subView, setSubView, primary }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const anyActive = items.some((t) => t.key === subView);

  return (
    <div className="relative h-full flex items-center" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 rounded-xl px-3 sm:px-3.5 py-2 text-sm font-semibold whitespace-nowrap transition ${anyActive || open ? "" : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"}`}
        style={anyActive || open ? { backgroundColor: `${primary}14`, color: primary } : undefined}
      >
        More
        <svg className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 z-[2000] w-52 bg-white rounded-2xl border border-gray-200 shadow-xl ring-1 ring-black/5 p-1.5">
          {items.map((t) => {
            const active = subView === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => { setOpen(false); setSubView(t.key); }}
                className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                style={active ? { background: `${primary}0f`, color: primary } : { color: "#374151" }}
                onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = `${primary}0f`; e.currentTarget.style.color = primary; } }}
                onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = ""; e.currentTarget.style.color = "#374151"; } }}
              >
                <span className="shrink-0" style={{ color: active ? primary : "#6b7280" }}>{t.icon}</span>
                <span className="flex-1 text-left truncate">{t.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function matchesFilter(key, p) {
  switch (key) {
    case "image":
      return p.post_type === 2;
    case "poll":
      return p.post_type === 3;
    case "video":
      return p.post_type === 4;
    case "audio":
      return p.post_type === 5;
    case "text":
      return !p.post_type || p.post_type === 1;
    default:
      return true; // "all"
  }
}

function readMember(token) {
  try {
    return JSON.parse(localStorage.getItem(`cm_${token}`) || "null");
  } catch {
    return null;
  }
}

/**
 * Community group feed (shell).
 *
 * /:token/feed/:groupToken — guruai-style 3-column layout:
 * left = about the group, center = composer + feed, right = leaderboard.
 * Posting is a placeholder until a community_posts backend exists.
 */
function CommunityFeed({ token: propToken, groupToken: propGroupToken, refCode, focusPostId, onMemberChange, onBack, navTabs, onNavigate, onInstructor, page, onChangePage, policies, communityName, myGroups = [], onSwitchGroup, onDiscoverGroups, template = "classic" }) {
  const params = useParams();
  const navigate = useNavigate();
  const token = propToken ?? params.token;
  const groupToken = propGroupToken ?? params.groupToken;

  // Embedded (inline on the community page) vs standalone route.
  const goBack = onBack ?? (() => navigate(`/${token}`));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [member, setMember] = useState(() => readMember(token));
  const [showProfile, setShowProfile] = useState(false);
  // In-group sub-view: "feed" | "members" | "leaderboard" | "rewards".
  // These are per-group and only reachable here (i.e. after the member joined).
  // When the parent drives navigation via the URL (?page=), the active section
  // and its setter come from the `page`/`onChangePage` props so the header
  // highlight stays in sync with the address bar; otherwise it's local state.
  const [localSubView, setLocalSubView] = useState("feed");
  const subView = onChangePage ? page || "feed" : localSubView;
  const setSubView = onChangePage || setLocalSubView;
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);

  const primary = data?.community?.primary_color || readTheme(token) || "#0274F7";

  // Tab title: the community name (falls back to FlexiCommunity) — otherwise
  // the browser shows the raw URL in the tab.
  useEffect(() => {
    document.title = data?.community?.name || "FlexiCommunity";
  }, [data?.community?.name]);

  // Signed-in member's standing — shared by the feed right rail "My Points"
  // card and the same card on the Members/Leaderboard pages (same source, so
  // the figures stay in sync).
  const { me: myStanding, pts: myPointsSettings, refresh: refreshMyPoints } = useMyPoints(token, groupToken, member);

  // Bumped whenever a point-earning action (e.g. profile photo upload) happens
  // while the member is on a self-fetching tab (Members/Leaderboard/Rewards), so
  // those views re-fetch and reflect the new points without a page reload.
  const [pointsVersion, setPointsVersion] = useState(0);
  const onPointsChanged = useCallback(() => {
    refreshMyPoints();
    setPointsVersion((v) => v + 1);
  }, [refreshMyPoints]);

  const signOut = () => {
    try {
      localStorage.removeItem(`cm_${token}`);
    } catch {
      /* ignore */
    }
    setMember(null);
    // Keep the parent (GroupCollection) member state in sync, otherwise the
    // community page it returns to still shows the signed-in UI until a refresh.
    onMemberChange?.(null);
    goBack();
  };

  const saveMember = (m) => {
    setMember(m);
    try {
      localStorage.setItem(`cm_${token}`, JSON.stringify(m));
    } catch {
      /* ignore */
    }
    onMemberChange?.(m);
  };

  // ----- In-place auth (no redirect to the landing page) -----
  const [authMode, setAuthMode] = useState(null); // null | "join" | "signin" | "forgot"
  // One-shot welcome flower shower shown right after a successful signup.
  const [welcome, setWelcome] = useState({ show: false, name: "" });
  // Closed-group rejection popup (member not on the uploaded allow-list).
  const [closedError, setClosedError] = useState("");

  // Pick up a celebration handed off from the community/group landing page: a
  // guest who just signed up there is redirected straight into this feed, so
  // the welcome shower should start on arrival rather than be lost across the
  // navigation (the landing page and this feed are separate component trees).
  useEffect(() => {
    let pending = null;
    try {
      pending = sessionStorage.getItem(`cwelcome_${token}`);
      if (pending !== null) sessionStorage.removeItem(`cwelcome_${token}`);
    } catch {
      /* sessionStorage unavailable */
    }
    if (pending !== null) setWelcome({ show: true, name: pending || "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [authForm, setAuthForm] = useState(EMPTY_FORM);
  const [authBusy, setAuthBusy] = useState(false);
  const [authMsg, setAuthMsg] = useState({ type: "", text: "" });
  // Forgot-password OTP flow: step 1 email → 2 code → 3 new password.
  const [forgotStep, setForgotStep] = useState(1);
  const [resetToken, setResetToken] = useState("");

  const openAuth = (mode) => {
    setAuthMsg({ type: "", text: "" });
    setAuthForm(EMPTY_FORM);
    setForgotStep(1);
    setResetToken("");
    setAuthMode(mode);
  };
  const closeAuth = () => !authBusy && setAuthMode(null);

  // ----- Referral tracking -----
  // The ?ref= code is persisted per community for the session so it survives tab
  // switches in the auth modal and is still available at signup time.
  useEffect(() => {
    if (refCode) {
      try {
        sessionStorage.setItem(`cref_${token}`, refCode);
      } catch {
        /* ignore */
      }
    }
  }, [refCode, token]);
  const getRef = () => {
    if (refCode) return refCode;
    try {
      return sessionStorage.getItem(`cref_${token}`) || undefined;
    } catch {
      return undefined;
    }
  };

  // After sign-in/sign-up on a group page, join the member to this group so the
  // referral link drops them straight into the group. Idempotent server-side.
  const autoJoinGroup = async (m) => {
    if (!groupToken || !m?.auth_token) return null;
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-group-join`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: m.community_member_id,
        member_token: m.auth_token,
        ref: getRef(),
      });
      return res.data || null;
    } catch {
      /* ignore — membership stays joinable from the group card */
      return null;
    }
  };

  const submitJoin = async (e) => {
    e.preventDefault();
    // GDPR: block signup if consent is mandatory and not given.
    if (community?.gdpr?.enabled && community?.gdpr?.mandatory && !authForm.gdpr_consent) {
      return setAuthMsg({ type: "error", text: "Please accept the Privacy and Terms to continue." });
    }
    // Phone: block signup if the community made the phone number mandatory.
    if (Number(community?.phone_required) === 1 && !String(authForm.phone || "").replace(/\D/g, "")) {
      return setAuthMsg({ type: "error", text: "Please enter your phone number to continue." });
    }
    setAuthBusy(true);
    setAuthMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-join`, {
        community_token: token,
        group_token: groupToken,
        ref: getRef(),
        ...authForm,
      });
      if (res.data?.error === 0) {
        const m = res.data.member;
        saveMember(m);
        setAuthMode(null);
        const joinRes = await autoJoinGroup(m);
        // Closed group: member's email isn't on the allow-list → show the popup
        // and DO NOT celebrate (no flower animation).
        if (joinRes?.closed_group) {
          setClosedError(joinRes.msg || "");
        } else if (!joinRes?.pending) {
          // New customer onboarding: celebrate with a flower shower — but not
          // when the group needs admin approval, since the member is pending.
          setWelcome({ show: true, name: m?.name || "" });
        }
        await load();
      } else if (res.data?.closed_group) {
        // Closed group + email not on the allow-list: block registration —
        // close the form and show the reason in a popup.
        setAuthMode(null);
        setClosedError(res.data?.msg || "");
      } else {
        setAuthMsg({ type: "error", text: res.data?.msg || "Could not join." });
      }
    } catch (err) {
      if (err.response?.data?.closed_group) {
        setAuthMode(null);
        setClosedError(err.response.data.msg || "");
      } else {
        setAuthMsg({ type: "error", text: err.response?.data?.msg || "Something went wrong. Please try again." });
      }
    } finally {
      setAuthBusy(false);
    }
  };

  const submitSignin = async (e) => {
    e.preventDefault();
    setAuthBusy(true);
    setAuthMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-login`, {
        community_token: token,
        email: authForm.email,
        password: authForm.password,
      });
      if (res.data?.error === 0) {
        const m = res.data.member;
        saveMember(m);
        const joinRes = await autoJoinGroup(m);
        await load();
        setAuthMode(null);
        if (joinRes?.closed_group) {
          setClosedError(joinRes.msg || "");
        } else {
          toast.success(`Welcome back, ${m?.name || ""}!`.trim());
        }
      } else {
        setAuthMsg({ type: "error", text: res.data?.msg || "Invalid email or password." });
      }
    } catch (err) {
      setAuthMsg({ type: "error", text: err.response?.data?.msg || "Something went wrong. Please try again." });
    } finally {
      setAuthBusy(false);
    }
  };

  // 3-step OTP reset: send code → verify code → set new password.
  const submitForgot = async (e) => {
    e.preventDefault();
    setAuthBusy(true);
    setAuthMsg({ type: "", text: "" });
    try {
      if (forgotStep === 1) {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-send-otp`, {
          community_token: token,
          email: authForm.email,
        });
        if (res.data?.success) {
          setResetToken(res.data.token || "");
          if (res.data.otp) setAuthForm((f) => ({ ...f, otp: res.data.otp }));
          setForgotStep(2);
        } else {
          setAuthMsg({ type: "error", text: res.data?.message || "Could not send the code." });
        }
      } else if (forgotStep === 2) {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-verify-otp`, {
          community_token: token,
          email: authForm.email,
          otp: authForm.otp,
          token: resetToken,
        });
        if (res.data?.success) {
          setForgotStep(3);
        } else {
          setAuthMsg({ type: "error", text: res.data?.message || "Invalid code." });
        }
      } else {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-reset-password`, {
          community_token: token,
          email: authForm.email,
          otp: authForm.otp,
          token: resetToken,
          password: authForm.password,
          password_confirmation: authForm.password_confirmation,
        });
        if (res.data?.success) {
          setForgotStep(1);
          setAuthMsg({ type: "success", text: res.data?.message || "Your password has been reset." });
        } else {
          setAuthMsg({ type: "error", text: res.data?.message || "Could not reset your password." });
        }
      }
    } catch (err) {
      setAuthMsg({
        type: "error",
        text: err.response?.data?.message || err.response?.data?.msg || "Something went wrong. Please try again.",
      });
    } finally {
      setAuthBusy(false);
    }
  };

  const [leaving, setLeaving] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const leaveGroup = async () => {
    setLeaving(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-group-leave`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id,
        member_token: member?.auth_token,
      });
      setConfirmLeave(false);
      toast.success(res.data?.msg || `You left ${group?.name || "the group"}.`);
      goBack();
    } catch (err) {
      toast.error(err.response?.data?.msg || "Could not leave the group. Please try again.");
      setLeaving(false);
    }
  };

  // ----- Posts -----
  const [posts, setPosts] = useState([]);
  // Map of post_id -> AI-generated comment, surfaced live into the PostCard.
  const [aiComments, setAiComments] = useState({});
  // Approve Posts: member's "pending approval" notice + the admin review modal.
  const [pendingNotice, setPendingNotice] = useState(false);
  const [pendingList, setPendingList] = useState(null);
  const [pendingBusyId, setPendingBusyId] = useState(null);
  const [feedFilter, setFeedFilter] = useState("all");
  const [isAdmin, setIsAdmin] = useState(false); // current member is group admin/moderator (staff)
  const [isFullAdmin, setIsFullAdmin] = useState(false); // role 1 (Admin) only — can pin & approve posts
  const [myRoleName, setMyRoleName] = useState(""); // "Admin" | "Moderator" | "" for the current member
  const [mySpecialist, setMySpecialist] = useState(""); // optional moderator specialist tag
  const [tagLimit, setTagLimit] = useState(null); // max members this member can @tag (null = unlimited)
  const [roster, setRoster] = useState([]); // group members for @mention autocomplete
  const [postMentions, setPostMentions] = useState([]); // members tagged in the composer
  const [postsLoading, setPostsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false); // more (older) posts to load
  const [loadingMore, setLoadingMore] = useState(false);
  // Skool-style feed categories (per group) + the active pill (0 = All).
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(0);
  const [postCategory, setPostCategory] = useState(0); // category chosen in the composer

  // Deep-link to a single post (?post={id}, used by post / reply emails and the
  // notification bell). The scroll/refresh effect lives just below loadPosts so
  // it can re-fetch when the target post isn't in the loaded feed.
  const focusedDoneRef = useRef(null);
  const focusReloadedRef = useRef(null);
  const focusTimersRef = useRef([]); // pending re-scroll timers for the deep-linked post
  const [composer, setComposer] = useState("");
  const [posting, setPosting] = useState(false);
  const [postImages, setPostImages] = useState([]); // File[] — multiple photos per post
  const [postImagePreviews, setPostImagePreviews] = useState([]); // object URLs, parallel to postImages
  const composerFileRef = useRef(null);
  const composerAudioRef = useRef(null);
  const composerVideoRef = useRef(null);
  const [pollMode, setPollMode] = useState(false);
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [videoFile, setVideoFile] = useState(null);
  const [videoFilePreview, setVideoFilePreview] = useState("");
  const [postAudio, setPostAudio] = useState(null);
  const [postAudioPreview, setPostAudioPreview] = useState("");
  const [postGif, setPostGif] = useState("");
  // Skool-style composer: a trigger row opens this "Create Post" modal.
  const [composerOpen, setComposerOpen] = useState(false);
  // Which upload drop-zone the modal shows: 'photo' | 'audio' | 'video' | null.
  const [mediaMode, setMediaMode] = useState(null);

  const pickPostGif = (url) => {
    if (!url) return;
    clearPostImage();
    clearPostAudio();
    clearVideoFile();
    setPollMode(false);
    setMediaMode("photo");
    setPostGif(url);
  };
  const clearPostGif = () => setPostGif("");

  const MAX_POST_IMAGES = 10;
  const pickPostImages = (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) => f && f.type.startsWith("image/"));
    if (!incoming.length) return;
    clearPostAudio();
    clearVideoFile();
    clearPostGif();
    const room = MAX_POST_IMAGES - postImages.length;
    if (room <= 0) {
      toast.warn(`You can add up to ${MAX_POST_IMAGES} photos.`);
      return;
    }
    const valid = [];
    for (const f of incoming) {
      if (f.size > 5 * 1024 * 1024) {
        toast.warn(`"${f.name}" is over 5 MB.`);
        continue;
      }
      valid.push(f);
    }
    const add = valid.slice(0, room);
    if (!add.length) return;
    if (valid.length > add.length) toast.warn(`You can add up to ${MAX_POST_IMAGES} photos.`);
    setPostImages([...postImages, ...add]);
    setPostImagePreviews([...postImagePreviews, ...add.map((f) => URL.createObjectURL(f))]);
  };
  const removePostImageAt = (idx) => {
    const url = postImagePreviews[idx];
    if (url) URL.revokeObjectURL(url);
    setPostImages(postImages.filter((_, i) => i !== idx));
    setPostImagePreviews(postImagePreviews.filter((_, i) => i !== idx));
    if (composerFileRef.current) composerFileRef.current.value = "";
  };
  const clearPostImage = () => {
    postImagePreviews.forEach((u) => URL.revokeObjectURL(u));
    setPostImages([]);
    setPostImagePreviews([]);
    if (composerFileRef.current) composerFileRef.current.value = "";
  };
  const pickPostAudio = (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/")) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.warn("Audio must be under 15 MB.");
      return;
    }
    clearPostImage();
    clearVideoFile();
    clearPostGif();
    setPollMode(false);
    setPostAudio(file);
    setPostAudioPreview(URL.createObjectURL(file));
  };
  const clearPostAudio = () => {
    setPostAudio(null);
    setPostAudioPreview("");
    if (composerAudioRef.current) composerAudioRef.current.value = "";
  };
  const pickVideoFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.warn("Choose a video file.");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.warn("Video must be under 50 MB.");
      return;
    }
    clearPostImage();
    clearPostAudio();
    clearPostGif();
    setPollMode(false);
    setVideoFile(file);
    setVideoFilePreview(URL.createObjectURL(file));
  };
  const clearVideoFile = () => {
    setVideoFile(null);
    setVideoFilePreview("");
    if (composerVideoRef.current) composerVideoRef.current.value = "";
  };

  // Media-mode handlers shared by the trigger row and the modal toolbar. Each
  // switches the modal into that upload mode and shows its drop-zone — the file
  // picker only opens when the user clicks the drop-zone itself.
  const startPhoto = () => {
    setPollMode(false);
    clearPostAudio();
    clearVideoFile();
    clearPostGif();
    setMediaMode("photo");
  };
  const startAudio = () => {
    setPollMode(false);
    clearPostImage();
    clearVideoFile();
    clearPostGif();
    setMediaMode("audio");
  };
  const startVideo = () => {
    setPollMode(false);
    clearPostImage();
    clearPostAudio();
    clearPostGif();
    setMediaMode("video");
  };
  const togglePoll = () => {
    setMediaMode(null);
    clearPostImage();
    clearPostAudio();
    clearPostGif();
    clearVideoFile();
    setPollMode((v) => {
      if (!v) setPollOptions(["", ""]);
      return !v;
    });
  };

  // Open the Create Post modal, optionally jumping straight into a media mode.
  const openComposer = (mode = "text") => {
    setComposerOpen(true);
    if (mode === "photo") startPhoto();
    else if (mode === "audio") startAudio();
    else if (mode === "video") startVideo();
    else if (mode === "poll") {
      setMediaMode(null);
      setPollOptions(["", ""]);
      setPollMode(true);
    } else {
      setMediaMode(null);
      setPollMode(false);
    }
  };

  const loadPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-posts`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id || undefined,
        community_group_category_id: activeCategory || undefined,
        offset: 0,
      });
      if (res.data?.error === 0) {
        setPosts(res.data.posts || []);
        setHasMore(!!res.data.has_more);
        setIsAdmin(!!res.data.is_admin);
        setIsFullAdmin(!!res.data.is_full_admin);
        setMyRoleName(res.data.my_role_name || "");
        setMySpecialist(res.data.my_specialist || "");
        setTagLimit(res.data.tag_limit ?? null);
        setCategories(res.data.categories || []);
      }
    } catch {
      /* ignore */
    } finally {
      setPostsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, groupToken, member?.community_member_id, activeCategory]);

  // Load the next page of (older) posts and append them.
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-posts`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id || undefined,
        community_group_category_id: activeCategory || undefined,
        offset: posts.length,
      });
      if (res.data?.error === 0) {
        setPosts((prev) => [...prev, ...(res.data.posts || [])]);
        setHasMore(!!res.data.has_more);
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, hasMore, token, groupToken, member?.community_member_id, activeCategory, posts.length]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // Infinite scroll: auto-load older posts when the sentinel nears the viewport.
  // A "latest ref" holds loadMore so the observer attaches once per hasMore
  // toggle (no disconnect/reconnect churn) yet always calls the current loadMore.
  const loadMoreRef = useRef(null);
  const loadMoreFnRef = useRef(loadMore);
  useEffect(() => {
    loadMoreFnRef.current = loadMore;
  }, [loadMore]);
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasMore) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreFnRef.current?.();
      },
      { rootMargin: "600px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore]);

  // Group roster for @mention autocomplete (name + token + avatar).
  useEffect(() => {
    if (!token || !groupToken) {
      setRoster([]);
      return;
    }
    let active = true;
    axios
      .post(`${config.COMMUNITY_API_URL}/community-members`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id || undefined,
      })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        // Normalise to what MentionTextarea expects: the API exposes the member's
        // public id as `member_token`, but the picker inserts `token`.
        setRoster(
          (res.data.members || []).map((m) => ({
            name: m.name,
            img: m.img,
            token: m.token || m.member_token || m.url_token,
            is_admin: m.is_admin,
          }))
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [token, groupToken, member?.community_member_id]);

  // Default the composer's category to the first one the member can post in.
  useEffect(() => {
    if (!postCategory && categories.length) {
      const first = categories.find((c) => !c.admin_only || isAdmin) || categories[0];
      if (first) setPostCategory(first.community_group_category_id);
    }
  }, [categories, isAdmin, postCategory]);

  // Once the feed has loaded, scroll a deep-linked post into view (done once per
  // target). If the target isn't in the currently-loaded feed — e.g. a brand-new
  // post in the group we're already viewing, so loadPosts() wasn't re-triggered
  // by a groupToken change — refresh the feed once so it gets fetched, instead
  // of silently showing nothing until a hard refresh.
  // Reset the "already scrolled" state whenever the deep-link target changes, and
  // cancel any pending re-scroll timers.
  useEffect(() => {
    focusedDoneRef.current = null;
    focusReloadedRef.current = null;
    focusTimersRef.current.forEach(clearTimeout);
    focusTimersRef.current = [];
  }, [focusPostId]);

  useEffect(() => {
    if (!focusPostId || postsLoading) return;
    if (focusedDoneRef.current === focusPostId) return;
    const el = document.getElementById(`post-${focusPostId}`);
    if (el) {
      focusedDoneRef.current = focusPostId;
      // Align the post just below the sticky header (scroll-mt-24 on the wrapper).
      const scroll = (behavior) => {
        if (document.body.contains(el)) el.scrollIntoView({ behavior, block: "start" });
      };
      scroll("smooth");
      // Images (avatars + full-width galleries) load after the first scroll and
      // shift the layout — especially on mobile — so re-snap onto the post as each
      // image settles and at a few fixed checkpoints. Corrections use "auto" so
      // they land precisely without re-animating.
      el.querySelectorAll("img").forEach((img) => {
        if (!img.complete) {
          img.addEventListener("load", () => scroll("auto"), { once: true });
          img.addEventListener("error", () => scroll("auto"), { once: true });
        }
      });
      [150, 450, 900, 1600].forEach((ms) => focusTimersRef.current.push(setTimeout(() => scroll("auto"), ms)));
    } else if (hasMore && !loadingMore) {
      // Target isn't in the loaded feed yet — pull in more (older) posts until it
      // appears, instead of reloading the same first page.
      loadMore();
    } else if (focusReloadedRef.current !== focusPostId) {
      // No more pages to load — refresh once in case it's a brand-new post the
      // initial fetch missed.
      focusReloadedRef.current = focusPostId;
      loadPosts();
    }
  }, [focusPostId, postsLoading, posts, hasMore, loadingMore, loadMore, loadPosts]);

  // After a post is created, ask the backend to (maybe) add the group's
  // automatic AI reply. The endpoint self-checks the group setting and no-ops
  // when it's off, so this is safe to call for every new post. Best-effort:
  // bumps the post's comment count when a reply is generated.
  const triggerAiComment = useCallback(
    async (post) => {
      if (!post?.post_id) return;
      try {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-ai-comment`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member?.community_member_id,
          member_token: member?.auth_token,
          post_id: post.post_id,
        });
        if (res.data?.generated === 1 && res.data.comment) {
          // Surface the generated reply live (no page refresh): hand it to the
          // matching PostCard, which injects it into its comment thread.
          setAiComments((m) => ({ ...m, [post.post_id]: res.data.comment }));
          setPosts((ps) =>
            ps.map((p) =>
              p.post_id === post.post_id
                ? { ...p, comment_count: (p.comment_count || 0) + 1 }
                : p
            )
          );
        }
      } catch {
        /* best-effort — AI reply is non-critical */
      }
    },
    [token, groupToken, member?.community_member_id, member?.auth_token]
  );

  // Drop a freshly created post into the feed — unless the group holds member
  // posts for approval, in which case it isn't shown and we tell the member.
  const addCreatedPost = (res) => {
    if (res.data?.pending) {
      setPendingNotice(true);
      return;
    }
    // The create endpoint doesn't echo the category label; attach it from the
    // chosen category so the new post shows its tag immediately.
    let post = res.data.post;
    if (post && !post.category_name && postCategory) {
      const c = categories.find(
        (x) => Number(x.community_group_category_id) === Number(postCategory)
      );
      if (c) post = { ...post, category_id: c.community_group_category_id, category_name: c.name, category_emoji: c.emoji };
    }
    setPosts((ps) => [post, ...ps]);
    triggerAiComment(post);
  };

  // ----- Approve Posts (admin review of pending member posts) -----
  const loadPending = useCallback(async () => {
    setPendingList(null);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-pending-posts`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id,
        member_token: member?.auth_token,
      });
      setPendingList(res.data?.error === 0 ? res.data.posts || [] : []);
    } catch {
      setPendingList([]);
    }
  }, [token, groupToken, member?.community_member_id, member?.auth_token]);

  const reviewPending = async (post, action) => {
    setPendingBusyId(post.post_id);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-post-approve`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id,
        member_token: member?.auth_token,
        post_id: post.post_id,
        action,
      });
      if (res.data?.error === 0) {
        setPendingList((ps) => (ps || []).filter((x) => x.post_id !== post.post_id));
        if (action === "approve") {
          setPosts((ps) => [post, ...(ps || [])]);
          toast.success("Post approved.");
        } else {
          toast.info("Post rejected.");
        }
      } else {
        toast.error(res.data?.msg || "Could not update the post.");
      }
    } catch {
      toast.error("Could not update the post.");
    } finally {
      setPendingBusyId(null);
    }
  };

  // Load pending posts whenever the admin opens the Pending Posts page.
  useEffect(() => {
    if (subView === "pendingpost") loadPending();
  }, [subView, loadPending]);

  const createPost = async () => {
    // Convert picked @mentions to inline tokens for storage/notifications.
    const content = serializeMentions(composer, postMentions).trim();
    if (pollMode) {
      const opts = pollOptions.map((o) => o.trim()).filter(Boolean);
      if (!content) return toast.warn("Add a question for your poll.");
      if (opts.length < 2) return toast.warn("A poll needs at least 2 options.");
      setPosting(true);
      try {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-post-create`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member?.community_member_id,
          member_token: member?.auth_token,
          community_group_category_id: postCategory || undefined,
          content,
          poll_options: JSON.stringify(opts),
        });
        if (res.data?.error === 0) {
          addCreatedPost(res);
          setComposer(""); setPostMentions([]);
          setComposerOpen(false);
          refreshMyPoints();
          setPollMode(false);
          setPollOptions(["", ""]);
        } else {
          toast.error(res.data?.msg || "Could not create poll.");
        }
      } catch {
        /* ignore */
      } finally {
        setPosting(false);
      }
      return;
    }
    if (videoFile) {
      setPosting(true);
      try {
        const fd = new FormData();
        fd.append("community_token", token);
        fd.append("group_token", groupToken);
        fd.append("community_member_id", member?.community_member_id);
        fd.append("member_token", member?.auth_token);
        fd.append("community_group_category_id", postCategory || "");
        fd.append("content", content);
        fd.append("video", videoFile);
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-post-create`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.data?.error === 0) {
          addCreatedPost(res);
          setComposer(""); setPostMentions([]);
          setComposerOpen(false);
          refreshMyPoints();
          setMediaMode(null);
          clearVideoFile();
        } else {
          toast.error(res.data?.msg || "Could not post video.");
        }
      } catch {
        toast.error("Could not post video.");
      } finally {
        setPosting(false);
      }
      return;
    }
    if (postAudio) {
      setPosting(true);
      try {
        const fd = new FormData();
        fd.append("community_token", token);
        fd.append("group_token", groupToken);
        fd.append("community_member_id", member?.community_member_id);
        fd.append("member_token", member?.auth_token);
        fd.append("community_group_category_id", postCategory || "");
        fd.append("content", content);
        fd.append("audio", postAudio);
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-post-create`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.data?.error === 0) {
          addCreatedPost(res);
          setComposer(""); setPostMentions([]);
          setComposerOpen(false);
          refreshMyPoints();
          clearPostAudio();
        } else {
          toast.error(res.data?.msg || "Could not post audio.");
        }
      } catch {
        toast.error("Could not post audio.");
      } finally {
        setPosting(false);
      }
      return;
    }
    if (postGif) {
      setPosting(true);
      try {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-post-create`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member?.community_member_id,
          member_token: member?.auth_token,
          community_group_category_id: postCategory || undefined,
          content,
          image_url: postGif,
        });
        if (res.data?.error === 0) {
          addCreatedPost(res);
          setComposer(""); setPostMentions([]);
          setComposerOpen(false);
          refreshMyPoints();
          clearPostGif();
        } else {
          toast.error(res.data?.msg || "Could not post GIF.");
        }
      } catch {
        /* ignore */
      } finally {
        setPosting(false);
      }
      return;
    }
    const hasImages = postImages.length > 0;
    if (!content && !hasImages) return;
    // Also auto-detect a video link pasted directly in the text.
    const detectedVideo = !hasImages ? extractVideoUrl(content) : null;
    setPosting(true);
    try {
      let res;
      if (hasImages) {
        const fd = new FormData();
        fd.append("community_token", token);
        fd.append("group_token", groupToken);
        fd.append("community_member_id", member?.community_member_id);
        fd.append("member_token", member?.auth_token);
        fd.append("community_group_category_id", postCategory || "");
        fd.append("content", content);
        postImages.forEach((f) => fd.append("image[]", f));
        res = await axios.post(`${config.COMMUNITY_API_URL}/community-post-create`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(`${config.COMMUNITY_API_URL}/community-post-create`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member?.community_member_id,
          member_token: member?.auth_token,
          community_group_category_id: postCategory || undefined,
          content,
          ...(detectedVideo ? { video_url: detectedVideo } : {}),
        });
      }
      if (res.data?.error === 0) {
        addCreatedPost(res);
        setComposer(""); setPostMentions([]);
        setComposerOpen(false);
        refreshMyPoints();
        clearPostImage();
      }
    } catch {
      /* ignore */
    } finally {
      setPosting(false);
    }
  };


  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-feed`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id || undefined,
      });
      if (res.data?.error === 0) {
        setData(res.data);
        writeTheme(token, res.data.community?.primary_color);
        applyCommunityFont(res.data.community?.membership_font);
        applyCommunityDirection(res.data.community?.rtl);
      } else setError(res.data?.msg || "Unable to load this feed.");
    } catch (err) {
      setError(err.response?.data?.msg || "Something went wrong while loading the feed.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, groupToken]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the avatar/profile button in sync with the saved profile photo.
  useEffect(() => {
    const mid = member?.community_member_id;
    if (!mid) return;
    let active = true;
    axios
      .post(`${config.COMMUNITY_API_URL}/community-member-profile`, {
        community_token: token,
        community_member_id: mid,
      })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        const fresh = res.data.member;
        if (fresh && (fresh.img !== member?.img || fresh.name !== member?.name)) {
          saveMember({ ...member, name: fresh.name, email: fresh.email, img: fresh.img });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, member?.community_member_id]);

  if (loading) {
    return (
      <div className="relative min-h-screen bg-[#f7f7f8]">
        <PageLoader label="Loading feed…" primary={primary} />
        {/* Keep the welcome celebration visible even while the feed reloads
            right after signup (load() flips `loading`). */}
        <WelcomeFlowers show={welcome.show} name={welcome.name} onDone={() => setWelcome({ show: false, name: "" })} />
        <ClosedGroupErrorModal show={!!closedError} message={closedError} primary={primary} onClose={() => setClosedError("")} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f7f7f8] px-4 gap-3">
        <p className="text-red-500 text-center">{error}</p>
        <button onClick={goBack} className="text-sm underline" style={{ color: primary }}>
          ← Back to community
        </button>
      </div>
    );
  }

  const { community, group, total_members } = data;
  // Group settings can hide the audio / video post option from the composer.
  const audioDisabled = Number(group?.disable_audio_post) === 1;
  const videoDisabled = Number(group?.disable_video_post) === 1;
  // Per-group Group Settings can hide nav menus (incl. the About tab).
  const groupMenus = group?.menus || {};
  const aboutHidden = Number(groupMenus.about) === 1;
  // Nav tabs visible after applying the group's menu-hide settings.
  const visibleNavTabs = (navTabs || []).filter((t) => {
    const mk = TAB_MENU_KEY[t.key];
    return !(mk && Number(groupMenus[mk]) === 1);
  });

  // In-group nav style by template. "sidebar" (studio / catalog / campus) moves
  // the group sections into a left rail on desktop instead of horizontal top
  // tabs; "bottom nav" (creator) adds an app-like bar on mobile. Classic keeps
  // the horizontal top tabs.
  const feedSidebar =
    template === "studio" || template === "catalog" || template === "campus";
  const feedBottomNav = template === "creator";
  const sidebarNavItems = feedSidebar
    ? [
        ...visibleNavTabs,
        ...(aboutHidden ? [] : [NAV_ABOUT_ITEM]),
        ...(isFullAdmin ? [NAV_PENDING_ITEM] : []),
      ]
    : [];
  const BOTTOM_NAV_KEYS = ["groups", "feed", "courses", "members", "leaderboard"];
  const bottomNavItems = feedBottomNav
    ? visibleNavTabs.filter((t) => BOTTOM_NAV_KEYS.includes(t.key))
    : [];

  // Only surface filters that actually have posts; fall back to "all" if the
  // active filter no longer matches anything (e.g. its last post was deleted).
  const availableFilters = FEED_FILTERS.filter(
    (f) => f.key === "all" || posts.some((p) => matchesFilter(f.key, p))
  );
  const activeFilter = availableFilters.some((f) => f.key === feedFilter) ? feedFilter : "all";
  const visiblePosts = posts.filter((p) => matchesFilter(activeFilter, p));

  return (
    <TemplateContext.Provider value={template}>
    <div className={`min-h-screen flex flex-col bg-[#f7f7f8] overflow-x-clip${feedSidebar ? " lg:pl-64" : ""}${feedBottomNav ? " pb-16 lg:pb-0" : ""}`}>
      {/* Sidebar interior — group sections in a left rail (desktop only). */}
      {feedSidebar && (
        <aside className="hidden lg:flex fixed left-0 top-0 z-40 h-screen w-64 flex-col border-r border-gray-100 bg-white">
          <div className="flex h-16 items-center border-b border-gray-50 px-5">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Group</p>
              <h2 className="text-sm font-bold leading-snug text-gray-900 line-clamp-1">
                {group?.name}
              </h2>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-4">
            <nav className="space-y-1">
              {sidebarNavItems.map((t) => {
                const isGroups = t.key === "groups";
                const active = !isGroups && subView === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => (isGroups ? onNavigate?.("groups") : setSubView(t.key))}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${active ? "" : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"}`}
                    style={active ? { backgroundColor: `${primary}14`, color: primary } : undefined}
                  >
                    {t.icon}
                    {isGroups ? "Community" : t.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>
      )}
      {/* Top bar */}
      <header className="bg-white/85 backdrop-blur-md border-b border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sticky top-0 z-30">
        <div className="px-2.5 sm:px-6 h-16 flex items-center gap-1.5 sm:gap-3 lg:gap-4">
          <div className="flex items-center gap-1 sm:gap-3 min-w-0 shrink-0">
            <button
              onClick={() => goBack()}
              aria-label="Back to community"
              className="shrink-0 -ml-0.5 p-1 text-gray-500 hover:text-gray-900"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            {community.logo && String(community.logo).trim() ? (
              <img
                src={community.logo}
                alt={community.name}
                className="h-7 sm:h-8 w-auto max-w-[92px] min-[380px]:max-w-[120px] sm:max-w-[130px] object-contain cursor-pointer"
                onClick={() => goBack()}
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <img
                src={`${process.env.PUBLIC_URL || ""}/images/flexicommunity-logo.png`}
                alt="FlexiCommunity"
                className="h-6 sm:h-8 w-auto max-w-[112px] min-[380px]:max-w-[150px] sm:max-w-[170px] object-contain cursor-pointer"
                onClick={() => goBack()}
              />
            )}
            {/* Group switcher — jump between joined groups (shown for members). */}
            {member && (
              <GroupSwitcher
                groups={myGroups}
                currentToken={groupToken}
                primary={primary}
                onSelect={onSwitchGroup}
                onDiscover={onDiscoverGroups}
              />
            )}
          </div>

          {/* Group nav — per-group sections (member is already inside the group,
              so Members / Leaderboard / Rewards are gated behind group access). */}
          {!feedBottomNav && navTabs && (
            <div className="lg:hidden flex-1 min-w-0 flex justify-center">
              <GroupNavMenu navTabs={visibleNavTabs} showAbout={!aboutHidden} showPending={isFullAdmin} subView={subView} onNavigate={onNavigate} setSubView={setSubView} primary={primary} />
            </div>
          )}
          {!feedSidebar && navTabs && (() => {
            // Primary tabs stay inline like the reference design; the rest
            // (Map / Leaderboard / Rewards / About) collapse into a "More ▾" dropdown.
            const PRIMARY_KEYS = ["groups", "feed", "courses", "events", "members"];
            const primaryTabs = visibleNavTabs.filter((t) => PRIMARY_KEYS.includes(t.key));
            // About is appended unless the group's menu setting hides it.
            const overflowTabs = [
              ...visibleNavTabs.filter((t) => !PRIMARY_KEYS.includes(t.key)),
              ...(aboutHidden ? [] : [NAV_ABOUT_ITEM]),
              ...(isFullAdmin ? [NAV_PENDING_ITEM] : []),
            ];
            return (
              <nav className="hidden lg:flex flex-1 min-w-0 items-center justify-center gap-1 h-full">
                {primaryTabs.map((t) => {
                  const isGroups = t.key === "groups";
                  const active = !isGroups && subView === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() =>
                        isGroups ? onNavigate?.("groups") : setSubView(t.key)
                      }
                      className={`flex items-center gap-1.5 rounded-xl px-3 sm:px-3.5 py-2 text-sm font-semibold whitespace-nowrap transition ${active ? "" : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"}`}
                      style={active ? { backgroundColor: `${primary}14`, color: primary } : undefined}
                    >
                      {t.icon}
                      {isGroups ? "Community" : t.label}
                    </button>
                  );
                })}
                {/* Secondary sections (+ About) in a "More" dropdown. */}
                <MoreNavMenu items={overflowTabs} subView={subView} setSubView={setSubView} primary={primary} />
              </nav>
            );
          })()}

          <div className="shrink-0 ml-auto flex items-center gap-1 sm:gap-3">
            {member ? (
              <>
                <NotificationBell token={token} member={member} primary={primary} />
                <MemberMenu
                  member={member}
                  primary={primary}
                  onProfile={() => setShowProfile(true)}
                  onChangePassword={() => setShowChangePassword(true)}
                  onAccountSettings={() => setShowAccountSettings(true)}
                  onSignOut={signOut}
                />
              </>
            ) : (
              <button
                onClick={() => openAuth("signin")}
                style={{ backgroundColor: primary }}
                className="text-white text-sm font-semibold px-4 py-2 rounded-full hover:opacity-90 transition active:scale-95"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      {subView === "courses" ? (
        <>
          <div className="max-w-7xl mx-auto w-full px-4 py-8">
            <CoursesView template={template} token={token} groupToken={groupToken} primary={primary} member={member} group={group} onPointsChange={refreshMyPoints} groupCard={<GroupMiniBanner group={group} className="min-h-[180px] lg:h-full" />} pointsCard={<MyPointsCard me={myStanding} pts={myPointsSettings} token={token} groupToken={groupToken} primary={primary} member={member} roleName={myRoleName} specialist={mySpecialist} />} />
          </div>
        </>
      ) : subView === "about" ? (
        <>
          <div className="max-w-7xl mx-auto w-full px-4 pt-6">
            <InlineBanner token={token} groupToken={groupToken} placement="about_page" wrapperClass="space-y-3" />
          </div>
        <GroupLanding
          token={token}
          gid={groupToken}
          member={member}
          embedded
          // Joined members see their referral link here; non-joined / guests
          // see the Join button (handled inside GroupLanding via `referral`).
          referral={
            member && myStanding?.url_token
              ? { urlToken: myStanding.url_token, points: myPointsSettings?.viral_share || 0 }
              : null
          }
          onBack={() => setSubView("feed")}
          // "Start free trial" / "Join" on the About tab must actually join the
          // group (start the trial), not just switch tabs — otherwise the member
          // never becomes a member and the group keeps reopening to the landing.
          onJoin={async () => {
            if (!member) {
              openAuth("join"); // guest: sign up (auto-joins this group), then in
              return;
            }
            const joinRes = await autoJoinGroup(member); // signed-in: join / start trial
            await load(); // refresh membership state
            if (joinRes?.closed_group) {
              setClosedError(joinRes.msg || ""); // blocked: popup, stay on landing
              return;
            }
            setSubView("feed"); // enter the group feed
          }}
          onLogin={() => openAuth("signin")}
          onInstructor={onInstructor}
        />
        </>
      ) : subView === "members" ? (
        <>
          <div className="max-w-7xl mx-auto w-full px-4 py-8">
            <MembersView token={token} groupToken={groupToken} primary={primary} member={member} groupCard={<GroupMiniBanner group={group} className="min-h-[180px] lg:h-full" />} refreshKey={pointsVersion} chatHidden={!!groupMenus.chat} chatLocked={!!group?.chat_locked} chatLevelName={group?.chat_level_name || ""} />
          </div>
        </>
      ) : subView === "map" ? (
        <>
          <div className="max-w-7xl mx-auto w-full px-4 py-8">
            <MapView token={token} groupToken={groupToken} primary={primary} member={member} groupCard={<GroupMiniBanner group={group} className="min-h-[180px] lg:h-full" />} pointsCard={<MyPointsCard me={myStanding} pts={myPointsSettings} token={token} groupToken={groupToken} primary={primary} member={member} roleName={myRoleName} specialist={mySpecialist} />} />
          </div>
        </>
      ) : subView === "leaderboard" ? (
        <>
          <div className="max-w-7xl mx-auto w-full px-4 py-8">
            <LeaderboardView token={token} groupToken={groupToken} primary={primary} member={member} groupCard={<GroupMiniBanner group={group} className="min-h-[180px] lg:h-full" />} refreshKey={pointsVersion} />
          </div>
        </>
      ) : subView === "rewards" ? (
        <>
          <div className="max-w-7xl mx-auto w-full px-4 py-8">
            <RewardsView token={token} groupToken={groupToken} primary={primary} member={member} groupCard={<GroupMiniBanner group={group} className="min-h-[180px] lg:h-full" />} refreshKey={pointsVersion} />
          </div>
        </>
      ) : subView === "events" ? (
        <>
          <div className="max-w-7xl mx-auto w-full px-4 py-8">
            <EventsView token={token} groupToken={groupToken} primary={primary} member={member} groupCard={<GroupMiniBanner group={group} className="min-h-[180px] lg:h-full" />} />
          </div>
        </>
      ) : subView === "pendingpost" ? (
        <div className="max-w-7xl mx-auto w-full px-4 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <main className="lg:col-span-8 space-y-5">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <SectionHeader
                primary={primary}
                iconPath={PENDING_ICON_PATH}
                title="Pending posts"
                subtitle="Member posts awaiting your approval"
                trailing={pendingList == null ? "…" : `${pendingList.length} ${pendingList.length === 1 ? "post" : "posts"}`}
              />
            </div>
            {!isAdmin ? (
              <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-400">
                Only group admins can review pending posts.
              </div>
            ) : pendingList === null ? (
              <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-400">Loading…</div>
            ) : pendingList.length === 0 ? (
              <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-400">No posts awaiting approval.</div>
            ) : (
              pendingList.map((p) => (
                <div key={p.post_id} className="rounded-2xl border border-amber-200 bg-white shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between gap-3 border-b border-amber-100 bg-amber-50 px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d={PENDING_ICON_PATH} /></svg>
                      Pending approval
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => reviewPending(p, "reject")}
                        disabled={pendingBusyId === p.post_id}
                        className="rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50 disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => reviewPending(p, "approve")}
                        disabled={pendingBusyId === p.post_id}
                        style={{ backgroundColor: primary }}
                        className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                      >
                        {pendingBusyId === p.post_id ? "…" : "Approve"}
                      </button>
                    </div>
                  </div>
                  <PostCard
                    post={p}
                    template={template}
                    primary={primary}
                    member={member}
                    isAdmin={isAdmin}
                    isFullAdmin={isFullAdmin}
                    roster={roster}
                    tagLimit={tagLimit}
                    replyLocked={!!group?.reply_locked}
                    replyLevelName={group?.reply_level_name || ""}
                    token={token}
                    groupToken={groupToken}
                    onRequireAuth={() => openAuth("signin")}
                    onPointsChange={refreshMyPoints}
                    onDeleted={() => setPendingList((ps) => (ps || []).filter((x) => x.post_id !== p.post_id))}
                    onPinned={() => {}}
                  />
                </div>
              ))
            )}
          </main>
          <aside className="lg:col-span-4 space-y-4 lg:self-start lg:sticky lg:top-20">
            <GroupMiniBanner group={group} className="min-h-[180px] lg:h-full" />
          </aside>
        </div>
      ) : (
        <>
      {/* 2-column body (Skool layout) — feed 70% / rail 30%. The group banner
          now lives in the right rail (like the other group pages) instead of a
          full-width hero, so the feed leads the left column. */}
      <div className="max-w-7xl mx-auto w-full px-4 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Center — composer + feed */}
        <main className="lg:col-span-8 space-y-5">
          {/* Premium combined card — heading band + post composer in one card. */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <SectionHeader
              primary={primary}
              iconPath="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              title="Feed"
              subtitle="Latest posts in this group"
              trailing={
                posts == null
                  ? "…"
                  : (() => {
                      // Show the group's true total (the feed itself only loads
                      // the latest 50), so this matches the group banner count.
                      const total = group?.post_count ?? posts.length;
                      return `${total} ${total === 1 ? "post" : "posts"}`;
                    })()
              }
            />

          {/* Composer (inside the same card as the heading) */}
          {member ? (
            <>
              {/* Hidden file inputs — kept outside the modal so the trigger-row
                  Photo / Audio buttons can open the picker before it paints. */}
              <input
                ref={composerFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={(e) => pickPostImages(e.target.files)}
              />
              <input
                ref={composerAudioRef}
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={(e) => pickPostAudio(e.target.files?.[0])}
              />
              <input
                ref={composerVideoRef}
                type="file"
                accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-m4v"
                className="hidden"
                onChange={(e) => pickVideoFile(e.target.files?.[0])}
              />

              {/* Composer trigger row — compact (full options live in the modal) */}
              <div className="p-3 sm:p-3.5">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="shrink-0">
                    <Avatar name={member.name} img={member.img} primary={primary} size={36} />
                  </span>
                  <button
                    type="button"
                    onClick={() => openComposer("text")}
                    className="flex-1 min-w-0 text-left rounded-full bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm text-gray-400 hover:bg-gray-100 transition"
                  >
                    Write something…
                  </button>
                  {/* Quick post-type shortcuts. On mobile they sit in a subtle
                      rounded bar on a full-width second row — each an equal-width
                      icon+label button so the space is filled cleanly. On sm+
                      they collapse to icon-only squares inline after the input. */}
                  <div className="flex items-center gap-1 w-full mt-1 rounded-xl bg-gray-50 p-1 ring-1 ring-gray-100 sm:mt-0 sm:w-auto sm:gap-1.5 sm:rounded-none sm:bg-transparent sm:p-0 sm:ring-0 sm:shrink-0">
                    {[["photo", "Photo"], ["video", "Video"], ["audio", "Audio"], ["poll", "Poll"]]
                      .filter(([k]) => !(k === "audio" && audioDisabled) && !(k === "video" && videoDisabled))
                      .map(([k, label]) => {
                      const accent = COMPOSER_ACCENT[k] || primary;
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => openComposer(k)}
                          title={label}
                          aria-label={label}
                          className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg transition hover:scale-105 active:scale-95 sm:h-9 sm:w-9 sm:flex-none"
                          style={{ backgroundColor: `${accent}1a`, color: accent }}
                        >
                          <svg className="w-[18px] h-[18px] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                            <path strokeLinecap="round" strokeLinejoin="round" d={COMPOSER_ICONS[k]} />
                          </svg>
                          <span className="text-xs font-semibold sm:hidden">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Create Post modal */}
              {composerOpen && (
                <div
                  className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:py-10"
                  onClick={() => !posting && setComposerOpen(false)}
                >
                  <div
                    className="w-full max-w-xl rounded-2xl bg-white shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                      <h3 className="text-lg font-bold text-gray-900">Create Post</h3>
                      <button
                        type="button"
                        onClick={() => setComposerOpen(false)}
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 transition"
                        aria-label="Close"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <Avatar name={member.name} img={member.img} primary={primary} size={36} />
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{member.name}</div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1">
                            {myRoleName && (
                              <span
                                className="inline-block text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded-full"
                                style={{ backgroundColor: `${primary}1a`, color: primary }}
                              >
                                {myRoleName}
                              </span>
                            )}
                            {mySpecialist && (
                              <span
                                className="inline-block text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded-full"
                                style={{ border: `1px solid ${primary}59`, color: primary }}
                              >
                                {mySpecialist}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="relative">
                        <MentionTextarea
                          value={composer}
                          onChange={setComposer}
                          onAddMention={(m) => setPostMentions((list) => [...list, m])}
                          members={roster}
                          limit={tagLimit}
                          tagged={postMentions.length}
                          primary={primary}
                          rows={3}
                          autoFocus
                          autoGrow
                          placeholder="Type your message… use @ to tag members"
                          className="w-full resize-none text-[15px] bg-gray-50 border border-gray-200 rounded-xl pl-4 pr-11 py-3 outline-none focus:bg-white focus:border-gray-300 transition placeholder:text-gray-400 min-h-[110px] max-h-[45vh] overflow-y-auto"
                        />
                        <span className="absolute right-2 top-2">
                          <EmojiPicker
                            onPick={(e) => setComposer((c) => c + e)}
                            dropUp={false}
                            className="px-1.5 py-1.5 rounded-lg hover:bg-gray-100"
                          />
                        </span>
                      </div>

                  {/* Upload drop-zones for the active media mode */}
                  {mediaMode === "photo" && !postImagePreviews.length && !postGif && (
                    <MediaDropzone kind="photo" primary={primary} multiple onOpen={() => composerFileRef.current?.click()} onFile={pickPostImages} />
                  )}
                  {mediaMode === "audio" && !postAudioPreview && (
                    <MediaDropzone kind="audio" primary={primary} onOpen={() => composerAudioRef.current?.click()} onFile={pickPostAudio} />
                  )}
                  {mediaMode === "video" && !videoFilePreview && (
                    <MediaDropzone kind="video" primary={primary} onOpen={() => composerVideoRef.current?.click()} onFile={pickVideoFile} />
                  )}

                  {postImagePreviews.length > 0 && !pollMode && (
                    <div className="mt-2">
                      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                        {postImagePreviews.map((src, i) => (
                          <div key={i} className="relative aspect-square">
                            <img src={src} alt={`preview ${i + 1}`} className="h-full w-full rounded-xl border object-cover" />
                            <button
                              onClick={() => removePostImageAt(i)}
                              className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 text-sm text-white shadow"
                              aria-label={`Remove photo ${i + 1}`}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        {postImagePreviews.length < MAX_POST_IMAGES && (
                          <button
                            type="button"
                            onClick={() => composerFileRef.current?.click()}
                            className="flex aspect-square flex-col items-center justify-center rounded-xl border-2 border-dashed text-xs font-medium"
                            style={{ borderColor: `${primary}80`, color: primary }}
                            aria-label="Add more photos"
                          >
                            <span className="text-2xl leading-none">+</span>
                            Add
                          </button>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-400">{postImagePreviews.length}/{MAX_POST_IMAGES} photos</p>
                    </div>
                  )}

                  {postGif && (
                    <div className="relative mt-2 inline-block">
                      <img src={postGif} alt="gif" className="max-h-48 rounded-xl border" />
                      <button
                        onClick={clearPostGif}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-900 text-white text-sm flex items-center justify-center shadow"
                        aria-label="Remove gif"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {/* Audio preview (premium player chip) */}
                  {postAudioPreview && (
                    <div className="relative mt-3 flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <span
                        className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-white"
                        style={{ backgroundColor: primary }}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M9 18V5l12-2v13" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="6" cy="18" r="3" />
                          <circle cx="18" cy="16" r="3" />
                        </svg>
                      </span>
                      <audio controls src={postAudioPreview} className="h-9 flex-1 min-w-0" />
                      <button
                        onClick={clearPostAudio}
                        className="shrink-0 w-6 h-6 rounded-full bg-gray-900 text-white text-sm flex items-center justify-center shadow"
                        aria-label="Remove audio"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {videoFilePreview && (
                    <div className="relative mt-3">
                      <video src={videoFilePreview} controls className="w-full max-h-72 rounded-xl border bg-black" />
                      <button
                        onClick={clearVideoFile}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-900 text-white text-sm flex items-center justify-center shadow"
                        aria-label="Remove video"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {pollMode && (
                    <div className="mt-3 space-y-2.5">
                      {pollOptions.map((opt, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          <span
                            className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold"
                            style={{ backgroundColor: `${primary}14`, color: primary }}
                          >
                            {i + 1}
                          </span>
                          <input
                            value={opt}
                            onChange={(e) =>
                              setPollOptions((o) => o.map((v, idx) => (idx === i ? e.target.value : v)))
                            }
                            placeholder={`Option ${i + 1}`}
                            className={INPUT_CLS + " flex-1"}
                            {...themeFocus(primary)}
                          />
                          {pollOptions.length > 2 && (
                            <button
                              type="button"
                              onClick={() => setPollOptions((o) => o.filter((_, idx) => idx !== i))}
                              className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
                              aria-label="Remove option"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                      {pollOptions.length < 6 && (
                        <button
                          type="button"
                          onClick={() => setPollOptions((o) => [...o, ""])}
                          className="flex items-center gap-2 text-sm font-semibold pl-1 pt-0.5 hover:opacity-80 transition"
                          style={{ color: primary }}
                        >
                          <span className="w-7 h-7 rounded-full border-2 border-dashed flex items-center justify-center" style={{ borderColor: `${primary}66` }}>
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                            </svg>
                          </span>
                          Add option
                        </button>
                      )}
                    </div>
                  )}

                      {/* Media toolbar — Photo / Audio / Video / Poll (+ GIF) */}
                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        <ComposerActionButton icon="photo" label="Photo" primary={primary} active={mediaMode === "photo" || postImagePreviews.length > 0} onClick={startPhoto} />
                        {!audioDisabled && (
                          <ComposerActionButton icon="audio" label="Audio" primary={primary} active={mediaMode === "audio" || !!postAudio} onClick={startAudio} />
                        )}
                        {!videoDisabled && (
                          <ComposerActionButton icon="video" label="Video" primary={primary} active={mediaMode === "video" || !!videoFile} onClick={startVideo} />
                        )}
                        <ComposerActionButton icon="poll" label="Poll" primary={primary} active={pollMode} onClick={togglePoll} />
                        <GifPicker
                          onPick={pickPostGif}
                          dropUp={false}
                          className="rounded-xl border border-gray-200 px-2.5 py-2 hover:bg-gray-50"
                        />
                      </div>

                      {/* Category picker — files the post under a feed category. */}
                      {categories.length > 0 && (
                        <div className="mt-4">
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            Category
                          </label>
                          <select
                            value={postCategory || ""}
                            onChange={(e) => setPostCategory(Number(e.target.value) || 0)}
                            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 focus:border-transparent focus:outline-none focus:ring-2"
                            style={{ "--tw-ring-color": primary }}
                          >
                            {categories
                              .filter((c) => !c.admin_only || isAdmin)
                              .map((c) => (
                                <option
                                  key={c.community_group_category_id}
                                  value={c.community_group_category_id}
                                >
                                  {c.emoji ? `${c.emoji} ` : ""}
                                  {c.name}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                      <button
                        onClick={createPost}
                        disabled={
                          posting ||
                          (pollMode
                            ? false
                            : !composer.trim() && !postImages.length && !postAudio && !postGif && !videoFile)
                        }
                        style={{ backgroundColor: primary }}
                        className="mt-4 w-full text-white text-[15px] font-bold py-2.5 rounded-xl hover:opacity-90 transition disabled:opacity-40"
                      >
                        {posting
                          ? "Posting…"
                          : pollMode
                          ? "Create poll"
                          : videoFile
                          ? "Post video"
                          : postAudio
                          ? "Post audio"
                          : "Post"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="border-t border-gray-100 p-5 text-center text-sm text-gray-500">
              <button onClick={() => openAuth("join")} className="font-semibold" style={{ color: primary }}>
                Join the community
              </button>{" "}
              to post and comment.
            </div>
          )}
          </div>
          {/* /Premium combined card */}

          {/* Next upcoming event — premium banner, opens a detail popup */}
          <UpcomingEventBanner token={token} groupToken={groupToken} primary={primary} />

          {/* Categories + Filter on a single row. Category pills fit inline with
              a "More" dropdown for overflow; the post-type Filter sits at the end. */}
          {!postsLoading && (categories.length > 0 || availableFilters.length > 1) && (
            <div className="flex items-center gap-2">
              {categories.length > 0 ? (
                <CategoryBar
                  categories={categories}
                  active={activeCategory}
                  onSelect={setActiveCategory}
                  primary={primary}
                  className="flex-1 min-w-0"
                />
              ) : (
                <div className="flex-1" />
              )}
              {availableFilters.length > 1 && (
                <FeedFilter
                  items={availableFilters.map((f) => ({
                    ...f,
                    count: f.key === "all" ? posts.length : posts.filter((p) => matchesFilter(f.key, p)).length,
                  }))}
                  active={activeFilter}
                  onChange={setFeedFilter}
                  primary={primary}
                />
              )}
            </div>
          )}

          {/* Feed */}
          {postsLoading ? (
            <FeedSkeleton count={3} />
          ) : posts.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm py-16 px-6 text-center flex flex-col items-center">
              {/* Themed illustration — a translucent post card with a solid
                  "compose" badge, all tinted with the community colour. */}
              <svg viewBox="0 0 120 120" className="w-28 h-28 mb-5" style={{ color: primary }} fill="none">
                <circle cx="60" cy="60" r="56" fill="currentColor" opacity="0.07" />
                <rect x="28" y="32" width="64" height="48" rx="10" fill="currentColor" opacity="0.14" />
                <rect x="38" y="44" width="22" height="6" rx="3" fill="currentColor" opacity="0.55" />
                <rect x="38" y="56" width="44" height="5" rx="2.5" fill="currentColor" opacity="0.3" />
                <rect x="38" y="66" width="34" height="5" rx="2.5" fill="currentColor" opacity="0.3" />
                <circle cx="86" cy="84" r="17" fill="currentColor" />
                <path d="M86 77v14M79 84h14" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" />
              </svg>
              <h3 className="font-bold text-gray-900 text-lg">No posts yet</h3>
              <p className="text-sm text-gray-500 mt-1.5 max-w-xs">
                Be the first to share something with the group and get the conversation started.
              </p>
            </div>
          ) : (
            visiblePosts.map((p) => {
              const focused = focusPostId && String(p.post_id) === String(focusPostId);
              return (
                <div
                  key={p.post_id}
                  id={`post-${p.post_id}`}
                  className="scroll-mt-24 rounded-2xl transition-shadow"
                  style={focused ? { boxShadow: `0 0 0 2px ${primary}` } : undefined}
                >
                  <PostCard
                    post={p}
                    aiComment={aiComments[p.post_id]}
                    template={template}
                    primary={primary}
                    member={member}
                    isAdmin={isAdmin}
                    isFullAdmin={isFullAdmin}
                    roster={roster}
                    tagLimit={tagLimit}
                    replyLocked={!!group?.reply_locked}
                    replyLevelName={group?.reply_level_name || ""}
                    token={token}
                    groupToken={groupToken}
                    onRequireAuth={() => openAuth("signin")}
                    onPointsChange={refreshMyPoints}
                    onDeleted={() => setPosts((ps) => ps.filter((x) => x.post_id !== p.post_id))}
                    onPinned={(pinned) =>
                      setPosts((ps) =>
                        [...ps.map((x) => (x.post_id === p.post_id ? { ...x, pinned } : x))].sort(
                          (a, b) => b.pinned - a.pinned || b.post_id - a.post_id
                        )
                      )
                    }
                  />
                </div>
              );
            })
          )}

          {/* Infinite-scroll sentinel — the observer auto-loads older posts as it
              nears the viewport; the button is a reliable manual fallback. */}
          {hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-6">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2 text-sm font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50 disabled:opacity-60"
              >
                {loadingMore ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Loading more posts…
                  </>
                ) : (
                  "Load more posts"
                )}
              </button>
            </div>
          )}
        </main>

        {/* Right rail — Skool-style group card + leaderboard. The whole rail is a
            sticky sidebar (self-start so it hugs its content instead of stretching
            to the feed's height) that scrolls internally when it's taller than the
            viewport, so every card stays reachable while reading a long feed. */}
        <aside className="lg:col-span-4 space-y-4 lg:self-start lg:sticky lg:top-20 lg:max-h-[calc(100vh-5rem)] lg:overflow-y-auto lg:pr-1 scrollbar-hide">
          {/* Group banner — same design as the course detail page, on the right. */}
          <GroupMiniBanner group={group} className="min-h-[180px] lg:h-full" />

          {/* My Points — mirrors the Members/Leaderboard pages, kept in sync via useMyPoints */}
          <MyPointsCard me={myStanding} pts={myPointsSettings} token={token} groupToken={groupToken} primary={primary} member={member} roleName={myRoleName} specialist={mySpecialist} />

          {/* Banner ad (group feed only) */}
          <InlineBanner token={token} groupToken={groupToken} placement="group_page" wrapperClass="space-y-3" />
        </aside>
      </div>
        </>
      )}

      {showProfile && member && (
        <ProfileModal
          token={token}
          member={member}
          groupToken={groupToken}
          primary={primary}
          banner={community?.banner}
          onClose={() => setShowProfile(false)}
          onUpdated={saveMember}
          onPointsChange={onPointsChanged}
        />
      )}

      {showChangePassword && member && (
        <ChangePasswordModal
          token={token}
          member={member}
          primary={primary}
          banner={community?.banner}
          onClose={() => setShowChangePassword(false)}
        />
      )}

      {showAccountSettings && member && (
        <AccountSettingsModal
          token={token}
          member={member}
          primary={primary}
          banner={community?.banner}
          onClose={() => setShowAccountSettings(false)}
        />
      )}

      {authMode && (
        <AuthModal
          mode={authMode}
          community={community}
          primary={primary}
          form={authForm}
          setForm={setAuthForm}
          busy={authBusy}
          msg={authMsg}
          onClose={closeAuth}
          onSwitch={openAuth}
          forgotStep={forgotStep}
          onSubmitJoin={submitJoin}
          onSubmitSignin={submitSignin}
          onSubmitForgot={submitForgot}
        />
      )}

      <WelcomeFlowers show={welcome.show} name={welcome.name} onDone={() => setWelcome({ show: false, name: "" })} />
      <ClosedGroupErrorModal show={!!closedError} message={closedError} primary={primary} onClose={() => setClosedError("")} />

      {/* Mobile-only floating group-info bubble. Hidden on all pages per
          request — flip SHOW_GROUP_INFO_BUBBLE to true to bring it back. */}
      {SHOW_GROUP_INFO_BUBBLE && subView !== "about" && subView !== "map" && (
        <GroupInfoBubble group={group} totalMembers={total_members} primary={primary} />
      )}

      <PolicyFooter
        policies={data.community?.policies || policies}
        communityName={communityName || data.community?.name}
        primary={primary}
        showBranding={data.community?.show_branding}
      />

      <BannerPopup token={token} groupToken={groupToken} placement={SUBVIEW_PLACEMENT[subView] || "group_page"} />

      {/* ---------- "Post pending approval" notice (member) ---------- */}
      {pendingNotice && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/50 p-4" onClick={() => setPendingNotice(false)}>
          <div className="relative w-full max-w-md rounded-2xl bg-white p-7 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPendingNotice(false)} className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-500 hover:bg-amber-100">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-500">
              <svg className="h-9 w-9" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.01M10.29 3.86l-8.48 14.7A1.5 1.5 0 003.11 21h17.78a1.5 1.5 0 001.3-2.44l-8.48-14.7a1.5 1.5 0 00-2.62 0z" /></svg>
            </div>
            <p className="text-[15px] leading-relaxed text-gray-700">
              Admin will review your post and approve. Until then your post to the group will be pending.
            </p>
          </div>
        </div>
      )}

      {/* ---------- Leave-group confirmation modal ---------- */}
      {confirmLeave && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => !leaving && setConfirmLeave(false)}
        >
          <div
            className="bg-white w-full max-w-sm rounded-2xl shadow-xl p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center">
              <svg className="w-7 h-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12H3m0 0l4-4m-4 4l4 4M13 4h5a2 2 0 012 2v12a2 2 0 01-2 2h-5" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mt-4">Leave group?</h3>
            <p className="text-sm text-gray-500 mt-1.5">
              You'll leave <span className="font-semibold text-gray-700">{group?.name}</span> and lose access to its
              feed. You can re-join anytime.
            </p>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setConfirmLeave(false)}
                disabled={leaving}
                className="flex-1 border border-gray-200 text-gray-700 text-sm font-semibold py-2.5 rounded-xl hover:bg-gray-50 transition disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={leaveGroup}
                disabled={leaving}
                className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-red-700 transition disabled:opacity-60"
              >
                {leaving && (
                  <Spinner size={16} />
                )}
                {leaving ? "Leaving…" : "Leave"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Creator interior: app-like bottom nav (mobile only). */}
      {feedBottomNav && (
        <nav className="fixed inset-x-0 bottom-0 z-30 flex justify-around border-t border-gray-100 bg-white py-1.5 lg:hidden">
          {bottomNavItems.map((t) => {
            const isGroups = t.key === "groups";
            const active = !isGroups && subView === t.key;
            return (
              <button
                key={t.key}
                onClick={() => (isGroups ? onNavigate?.("groups") : setSubView(t.key))}
                className="flex flex-col items-center gap-0.5 px-3 py-1 text-[10px] font-semibold"
                style={{ color: active ? primary : "#9ca3af" }}
              >
                {t.icon}
                {isGroups ? "Community" : t.label}
              </button>
            );
          })}
        </nav>
      )}
    </div>
    </TemplateContext.Provider>
  );
}

function videoEmbedUrl(url) {
  if (!url) return null;
  // YouTube
  let m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (m) return { type: "iframe", src: `https://www.youtube.com/embed/${m[1]}` };
  // Vimeo
  m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return { type: "iframe", src: `https://player.vimeo.com/video/${m[1]}` };
  // Wistia
  m = url.match(/(?:wistia\.com\/medias\/|wistia\.net\/(?:embed\/iframe|medias)\/|wi\.st\/medias\/)([\w-]+)/);
  if (m) return { type: "iframe", src: `https://fast.wistia.net/embed/iframe/${m[1]}` };
  // Direct video file (mp4, webm, etc.)
  if (/\.(mp4|webm|ogg|ogv|mov|m4v)(\?|$)/i.test(url)) return { type: "video", src: url };
  return { type: "link", src: url };
}

// Returns the first embeddable video URL found in free text, or null.
function extractVideoUrl(text) {
  if (!text) return null;
  const urls = text.match(/https?:\/\/[^\s]+/gi) || [];
  for (const u of urls) {
    const v = videoEmbedUrl(u.replace(/[)\].,]+$/, "")); // trim trailing punctuation
    if (v && (v.type === "iframe" || v.type === "video")) return u.replace(/[)\].,]+$/, "");
  }
  return null;
}

// Returns the first non-video URL in free text (for a link-preview card), or null.
function extractLinkUrl(text) {
  if (!text) return null;
  const urls = text.match(/https?:\/\/[^\s]+/gi) || [];
  for (const u of urls) {
    const clean = u.replace(/[)\].,]+$/, "");
    const v = videoEmbedUrl(clean);
    if (v && v.type === "link") return clean;
  }
  return null;
}

// Open Graph link-preview card for a pasted link.
function LinkPreview({ url }) {
  const [data, setData] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    setData(null);
    setDone(false);
    axios
      .post(`${config.COMMUNITY_API_URL}/community-link-preview`, { url })
      .then((res) => {
        if (active && res.data?.error === 0) setData(res.data);
      })
      .catch(() => {})
      .finally(() => active && setDone(true));
    return () => {
      active = false;
    };
  }, [url]);

  if (!done || !data) return null;
  // Nothing useful to show.
  if (!data.image && !data.title) return null;

  let host = data.site;
  try {
    host = new URL(url).hostname.replace(/^www\./, "");
  } catch {
    /* keep site */
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="mt-3 block border rounded-xl overflow-hidden hover:bg-gray-50 transition"
    >
      {data.image && (
        <img src={data.image} alt="" className="w-full max-h-72 object-cover bg-gray-100" />
      )}
      <div className="p-3">
        <p className="text-[11px] uppercase tracking-wide text-gray-400">{host}</p>
        {data.title && (
          <p className="text-sm font-semibold text-gray-900 mt-0.5 line-clamp-2">{data.title}</p>
        )}
        {data.description && (
          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{data.description}</p>
        )}
      </div>
    </a>
  );
}

// Renders text with bare URLs turned into clickable links.
// Long post bodies are clamped with a "Read more" toggle so the feed stays
// scannable; short posts render in full with no button. Truncation prefers the
// last word boundary so it never cuts mid-word, and links stay clickable.
function ReadMoreText({ text, className, primary, limit = 280 }) {
  const [expanded, setExpanded] = useState(false);
  const full = text || "";
  if (!full) return null;
  if (full.length <= limit) return <LinkedText text={full} className={className} primary={primary} />;

  let display = full;
  if (!expanded) {
    let cut = full.slice(0, limit);
    const lastSpace = cut.lastIndexOf(" ");
    if (lastSpace > limit * 0.6) cut = cut.slice(0, lastSpace);
    // Never cut inside a @[Name](token) mention marker.
    const openIdx = cut.lastIndexOf("@[");
    if (openIdx > cut.lastIndexOf(")")) cut = cut.slice(0, openIdx);
    display = cut.trimEnd() + "…";
  }
  return (
    <>
      <LinkedText text={display} className={className} primary={primary} />
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-1 text-sm font-semibold hover:underline focus:outline-none"
        style={{ color: primary }}
      >
        {expanded ? "Show less" : "Read more"}
      </button>
    </>
  );
}

function LinkedText({ text, className, primary = "#16a34a" }) {
  if (!text) return null;
  // Split on @mention tokens and URLs, keeping both as their own parts.
  const parts = String(text).split(/(@\[[^\]]+\]\([^)\s]+\)|https?:\/\/[^\s]+)/gi);
  return (
    <p className={className}>
      {parts.map((part, i) => {
        const mention = part && part.match(/^@\[([^\]]+)\]\([^)\s]+\)$/);
        if (mention) {
          return (
            <span key={i} className="font-semibold" style={{ color: primary }}>
              @{mention[1]}
            </span>
          );
        }
        if (/^https?:\/\//i.test(part)) {
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline break-all"
            >
              {part}
            </a>
          );
        }
        return part;
      })}
    </p>
  );
}

function VideoEmbed({ url }) {
  const v = videoEmbedUrl(url);
  if (!v) return null;
  if (v.type === "iframe") {
    return (
      <div className="mt-3 relative w-full rounded-xl overflow-hidden border bg-black" style={{ paddingTop: "56.25%" }}>
        <iframe
          src={v.src}
          title="video"
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  if (v.type === "video") {
    return <video src={v.src} controls className="mt-3 w-full max-h-[28rem] rounded-xl border bg-black" />;
  }
  return (
    <a href={v.src} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm underline text-blue-600 break-all">
      {v.src}
    </a>
  );
}

function Poll({ post, member, token, primary, onRequireAuth }) {
  const [poll, setPoll] = useState(post.poll);
  const [voting, setVoting] = useState(false);

  const vote = async (index) => {
    if (!member) return onRequireAuth?.();
    if (voting) return;
    setVoting(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-poll-vote`, {
        community_token: token,
        community_member_id: member.community_member_id,
        member_token: member?.auth_token,
        post_id: post.post_id,
        option_index: index,
      });
      if (res.data?.error === 0) setPoll(res.data.poll);
    } catch {
      /* ignore */
    } finally {
      setVoting(false);
    }
  };

  const total = poll.total_votes || 0;
  const voted = poll.my_vote !== null && poll.my_vote !== undefined;

  return (
    <div className="mt-3 space-y-2">
      {poll.options.map((o, i) => {
        const pct = total ? Math.round((o.votes / total) * 100) : 0;
        const mine = poll.my_vote === i;
        return (
          <button
            key={i}
            onClick={() => vote(i)}
            disabled={voting}
            className="relative w-full text-left rounded-lg border overflow-hidden disabled:cursor-default"
            style={{ borderColor: mine ? primary : "#e5e7eb" }}
          >
            {voted && (
              <div
                className="absolute inset-y-0 left-0"
                style={{ width: `${pct}%`, backgroundColor: `${primary}1f` }}
              />
            )}
            <div className="relative flex items-center justify-between px-3.5 py-2.5">
              <span className="text-sm text-gray-800 flex items-center gap-1.5">
                {mine && (
                  <svg className="w-4 h-4" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {o.text}
              </span>
              {voted && <span className="text-xs font-semibold text-gray-500">{pct}%</span>}
            </div>
          </button>
        );
      })}
      <p className="text-xs text-gray-400">
        {total} {total === 1 ? "vote" : "votes"}
        {!member && " · sign in to vote"}
      </p>
    </div>
  );
}

function Avatar({ name, img, primary, size = 40 }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <div
      className="rounded-full overflow-hidden flex items-center justify-center font-bold shrink-0"
      style={
        img
          ? { width: size, height: size }
          : { width: size, height: size, backgroundColor: `${primary}1f`, color: primary, border: `1px solid ${primary}40`, fontSize: size * 0.4 }
      }
    >
      {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : initial}
    </div>
  );
}

// Per-template post-card shape (radius / padding / shadow / hover). Border
// colour + pinned state are handled separately. classic = the original card.
const POST_SHAPE = {
  classic: "rounded-2xl shadow-sm p-5 hover:shadow-lg hover:-translate-y-0.5",
  studio: "rounded-3xl p-6 hover:shadow-md",
  catalog: "rounded-xl shadow-sm p-5 hover:shadow-md",
  campus: "rounded-2xl p-5",
  creator: "rounded-2xl shadow-sm p-4",
};

function PostCard({ post, aiComment, primary, member, isAdmin, isFullAdmin = false, replyLocked = false, replyLevelName = "", roster = [], tagLimit = null, token, groupToken, onRequireAuth, onPointsChange, onDeleted, onPinned, template = "classic" }) {
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.tot_likes);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentMentions, setCommentMentions] = useState([]);
  const [sending, setSending] = useState(false);
  const [commentImages, setCommentImages] = useState([]); // File[]
  const [commentImagePreviews, setCommentImagePreviews] = useState([]);
  const commentFileRef = useRef(null);
  const pickCommentImages = (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) => f && f.type.startsWith("image/"));
    if (!incoming.length) return;
    setCommentGif("");
    const room = MAX_MEDIA_IMAGES - commentImages.length;
    if (room <= 0) {
      toast.warn(`You can add up to ${MAX_MEDIA_IMAGES} photos.`);
      return;
    }
    const valid = [];
    for (const f of incoming) {
      if (f.size > 5 * 1024 * 1024) {
        toast.warn(`"${f.name}" is over 5 MB.`);
        continue;
      }
      valid.push(f);
    }
    const add = valid.slice(0, room);
    if (!add.length) return;
    if (valid.length > add.length) toast.warn(`You can add up to ${MAX_MEDIA_IMAGES} photos.`);
    setCommentImages([...commentImages, ...add]);
    setCommentImagePreviews([...commentImagePreviews, ...add.map((f) => URL.createObjectURL(f))]);
  };
  const removeCommentImageAt = (idx) => {
    const url = commentImagePreviews[idx];
    if (url) URL.revokeObjectURL(url);
    setCommentImages(commentImages.filter((_, i) => i !== idx));
    setCommentImagePreviews(commentImagePreviews.filter((_, i) => i !== idx));
    if (commentFileRef.current) commentFileRef.current.value = "";
  };
  const clearCommentImage = () => {
    commentImagePreviews.forEach((u) => URL.revokeObjectURL(u));
    setCommentImages([]);
    setCommentImagePreviews([]);
    if (commentFileRef.current) commentFileRef.current.value = "";
  };
  const [commentGif, setCommentGif] = useState("");
  const pickCommentGif = (url) => {
    if (!url) return;
    clearCommentImage();
    setCommentGif(url);
  };
  const [count, setCount] = useState(post.comment_count);
  // Automatic AI reply (arrives ~seconds after posting): inject it into the
  // comment thread live and auto-open it, so it shows without a page refresh.
  const aiInjectedRef = useRef(null);
  useEffect(() => {
    if (!aiComment || aiInjectedRef.current === aiComment.comment_id) return;
    aiInjectedRef.current = aiComment.comment_id;
    setCount((n) => n + 1);
    setShowComments(true);
    setComments((cs) => {
      if (cs === null) return [aiComment];
      if (cs.some((x) => x.comment_id === aiComment.comment_id)) return cs;
      return [...cs, aiComment];
    });
  }, [aiComment]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [content, setContent] = useState(post.content);
  const [images, setImages] = useState(
    Array.isArray(post.images) && post.images.length ? post.images : (post.image ? [post.image] : [])
  );
  const image = images[0] || null; // first photo — used by the edit UI / thumbnails
  const [video, setVideo] = useState(post.video);
  const [poll, setPoll] = useState(post.poll);
  const audio = post.audio;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteCmt, setConfirmDeleteCmt] = useState(null);
  const [deletingCmt, setDeletingCmt] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.content || "");
  const [savingEdit, setSavingEdit] = useState(false);
  const [newImage, setNewImage] = useState(null); // File chosen to replace
  const [newImagePreview, setNewImagePreview] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [editVideo, setEditVideo] = useState(post.video || "");
  const [editPollOptions, setEditPollOptions] = useState(post.poll?.options?.map((o) => o.text) || ["", ""]);
  const editFileRef = useRef(null);

  const isImagePost = post.post_type === 2;
  const isVideoPost = post.post_type === 4;
  const isPollPost = post.post_type === 3;
  const isOwner = member && post.member_id === member.community_member_id;
  // Admins/moderators can manage (edit/pin/delete) any post in the group.
  const canManage = isOwner || isAdmin;

  const startEdit = () => {
    setEditText(content || "");
    setNewImage(null);
    setNewImagePreview("");
    setRemoveImage(false);
    setEditVideo(video || "");
    setEditPollOptions(poll?.options?.map((o) => o.text) || ["", ""]);
    setEditing(true);
  };

  const pickEditImage = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) return toast.warn("Image must be under 5 MB.");
    setNewImage(file);
    setNewImagePreview(URL.createObjectURL(file));
    setRemoveImage(false);
  };

  const saveEdit = async () => {
    const text = editText.trim();
    const willHaveImage = isImagePost && !removeImage && (newImage || image);
    if (!text && !willHaveImage && !isVideoPost) return toast.warn("Post cannot be empty.");

    let payload;
    if (isVideoPost) {
      if (!editVideo.trim()) return toast.warn("Paste a video link.");
      payload = { content: text, video_url: editVideo.trim() };
    } else if (isPollPost) {
      const opts = editPollOptions.map((o) => o.trim()).filter(Boolean);
      if (!text) return toast.warn("Add a question for your poll.");
      if (opts.length < 2) return toast.warn("A poll needs at least 2 options.");
      payload = { content: text, poll_options: JSON.stringify(opts) };
    }

    setSavingEdit(true);
    try {
      let res;
      if (isImagePost && (newImage || removeImage)) {
        const fd = new FormData();
        fd.append("community_token", token);
        fd.append("community_member_id", member.community_member_id);
        fd.append("member_token", member?.auth_token);
        fd.append("post_id", post.post_id);
        fd.append("content", text);
        if (newImage) fd.append("image", newImage);
        if (removeImage) fd.append("remove_image", "1");
        res = await axios.post(`${config.COMMUNITY_API_URL}/community-post-edit`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(`${config.COMMUNITY_API_URL}/community-post-edit`, {
          community_token: token,
          community_member_id: member.community_member_id,
          member_token: member?.auth_token,
          post_id: post.post_id,
          content: text,
          ...(payload || {}),
        });
      }
      if (res.data?.error === 0) {
        setContent(res.data.content);
        if (res.data.images !== undefined) {
          // Cache-bust replaced photos so the browser re-fetches them.
          setImages(Array.isArray(res.data.images) ? res.data.images.map((u) => `${u}?t=${Date.now()}`) : []);
        } else if (res.data.image !== undefined) {
          setImages(res.data.image ? [`${res.data.image}?t=${Date.now()}`] : []);
        }
        if (res.data.video) setVideo(res.data.video);
        if (res.data.poll) setPoll(res.data.poll);
        setEditing(false);
      }
    } catch {
      /* ignore */
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    const h = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pinPost = async () => {
    setMenuOpen(false);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-post-pin`, {
        community_token: token,
        community_member_id: member.community_member_id,
        member_token: member?.auth_token,
        post_id: post.post_id,
      });
      if (res.data?.error === 0) onPinned?.(res.data.pinned);
    } catch {
      /* ignore */
    }
  };

  const deletePost = async () => {
    setDeleting(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-post-delete`, {
        community_token: token,
        community_member_id: member.community_member_id,
        member_token: member?.auth_token,
        post_id: post.post_id,
      });
      if (res.data?.error === 0) {
        toast.success("Post deleted.");
        onDeleted?.();
      } else {
        toast.error(res.data?.msg || "Could not delete the post.");
        setDeleting(false);
        setConfirmDelete(false);
      }
    } catch {
      toast.error("Could not delete the post.");
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const like = async () => {
    if (!member) return onRequireAuth?.();
    // optimistic
    setLiked((v) => !v);
    setLikes((n) => (liked ? n - 1 : n + 1));
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-post-like`, {
        community_token: token,
        community_member_id: member.community_member_id,
        member_token: member?.auth_token,
        post_id: post.post_id,
      });
      if (res.data?.error === 0) {
        setLiked(res.data.liked);
        setLikes(res.data.tot_likes);
        // Likes credit the post author — if that's me, update My Points now.
        if (isOwner) onPointsChange?.();
      }
    } catch {
      /* ignore */
    }
  };

  const openComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && comments === null) {
      setLoadingComments(true);
      try {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-comments`, {
          community_token: token,
          post_id: post.post_id,
          community_member_id: member?.community_member_id || undefined,
        });
        if (res.data?.error === 0) setComments(res.data.comments || []);
      } catch {
        setComments([]);
      } finally {
        setLoadingComments(false);
      }
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!member) return onRequireAuth?.();
    const cmt = serializeMentions(commentText, commentMentions).trim();
    if (!cmt && !commentImages.length && !commentGif) return;
    setSending(true);
    try {
      let res;
      if (commentImages.length) {
        const fd = new FormData();
        fd.append("community_token", token);
        fd.append("group_token", groupToken);
        fd.append("community_member_id", member.community_member_id);
        fd.append("member_token", member?.auth_token);
        fd.append("post_id", post.post_id);
        fd.append("cmt", cmt);
        commentImages.forEach((f) => fd.append("image[]", f));
        res = await axios.post(`${config.COMMUNITY_API_URL}/community-comment-create`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(`${config.COMMUNITY_API_URL}/community-comment-create`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member.community_member_id,
          member_token: member?.auth_token,
          post_id: post.post_id,
          cmt,
          ...(commentGif ? { image_url: commentGif } : {}),
        });
      }
      if (res.data?.error === 0) {
        setComments((cs) => [...(cs || []), res.data.comment]);
        setCount((n) => n + 1);
        setCommentText("");
        setCommentMentions([]);
        clearCommentImage();
        setCommentGif("");
        onPointsChange?.(); // commenting awards points — reflect it immediately
      }
    } catch {
      /* ignore */
    } finally {
      setSending(false);
    }
  };

  // --- Replies (single level: a reply attaches to a top-level comment) ---
  const [replyTo, setReplyTo] = useState(null); // top-level comment_id whose reply box is open
  const [replyText, setReplyText] = useState("");
  const [replyMentions, setReplyMentions] = useState([]);
  const [replySending, setReplySending] = useState(false);
  const [replyUploadPct, setReplyUploadPct] = useState(0); // 0-100 while uploading reply images
  const [expandedReplies, setExpandedReplies] = useState({}); // parentId -> show all replies
  // Only one reply box is open at a time, so a single image set is enough.
  const [replyImages, setReplyImages] = useState([]); // File[]
  const [replyImagePreviews, setReplyImagePreviews] = useState([]);
  const [replyGif, setReplyGif] = useState("");
  const replyFileRef = useRef(null);
  const pickReplyImages = (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) => f && f.type.startsWith("image/"));
    if (!incoming.length) return;
    setReplyGif("");
    const room = MAX_MEDIA_IMAGES - replyImages.length;
    if (room <= 0) {
      toast.warn(`You can add up to ${MAX_MEDIA_IMAGES} photos.`);
      return;
    }
    const valid = [];
    for (const f of incoming) {
      if (f.size > 5 * 1024 * 1024) {
        toast.warn(`"${f.name}" is over 5 MB.`);
        continue;
      }
      valid.push(f);
    }
    const add = valid.slice(0, room);
    if (!add.length) return;
    if (valid.length > add.length) toast.warn(`You can add up to ${MAX_MEDIA_IMAGES} photos.`);
    setReplyImages([...replyImages, ...add]);
    setReplyImagePreviews([...replyImagePreviews, ...add.map((f) => URL.createObjectURL(f))]);
  };
  const removeReplyImageAt = (idx) => {
    const url = replyImagePreviews[idx];
    if (url) URL.revokeObjectURL(url);
    setReplyImages(replyImages.filter((_, i) => i !== idx));
    setReplyImagePreviews(replyImagePreviews.filter((_, i) => i !== idx));
    if (replyFileRef.current) replyFileRef.current.value = "";
  };
  const clearReplyImages = () => {
    replyImagePreviews.forEach((u) => URL.revokeObjectURL(u));
    setReplyImages([]);
    setReplyImagePreviews([]);
    setReplyGif("");
    if (replyFileRef.current) replyFileRef.current.value = "";
  };
  const pickReplyGif = (url) => {
    if (!url) return;
    clearReplyImages();
    setReplyGif(url);
  };
  const submitReply = async (parentId) => {
    if (!member) return onRequireAuth?.();
    const cmt = serializeMentions(replyText, replyMentions).trim();
    if (!cmt && !replyImages.length && !replyGif) return;
    setReplySending(true);
    setReplyUploadPct(0);
    try {
      let res;
      if (replyImages.length) {
        const fd = new FormData();
        fd.append("community_token", token);
        fd.append("group_token", groupToken);
        fd.append("community_member_id", member.community_member_id);
        fd.append("member_token", member?.auth_token);
        fd.append("post_id", post.post_id);
        fd.append("cmt", cmt);
        fd.append("parent_id", parentId);
        replyImages.forEach((f) => fd.append("image[]", f));
        res = await axios.post(`${config.COMMUNITY_API_URL}/community-comment-create`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setReplyUploadPct(Math.round((e.loaded / e.total) * 100));
          },
        });
      } else {
        res = await axios.post(`${config.COMMUNITY_API_URL}/community-comment-create`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member.community_member_id,
          member_token: member?.auth_token,
          post_id: post.post_id,
          cmt,
          parent_id: parentId,
          ...(replyGif ? { image_url: replyGif } : {}),
        });
      }
      if (res.data?.error === 0) {
        setComments((cs) => [...(cs || []), res.data.comment]);
        setCount((n) => n + 1);
        setReplyText("");
        setReplyMentions([]);
        clearReplyImages();
        setReplyTo(null);
        onPointsChange?.();
      }
    } catch {
      /* ignore */
    } finally {
      setReplySending(false);
      setReplyUploadPct(0);
    }
  };

  const likeComment = async (c) => {
    if (!member) return onRequireAuth?.();
    setComments((cs) =>
      cs.map((x) =>
        x.comment_id === c.comment_id
          ? { ...x, liked: !x.liked, tot_likes: x.tot_likes + (x.liked ? -1 : 1) }
          : x
      )
    );
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-comment-like`, {
        community_token: token,
        community_member_id: member.community_member_id,
        member_token: member?.auth_token,
        comment_id: c.comment_id,
      });
      if (res.data?.error === 0) {
        setComments((cs) =>
          cs.map((x) =>
            x.comment_id === c.comment_id ? { ...x, liked: res.data.liked, tot_likes: res.data.tot_likes } : x
          )
        );
      }
    } catch {
      /* ignore */
    }
  };

  const deleteComment = async () => {
    const c = confirmDeleteCmt;
    if (!c) return;
    setDeletingCmt(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-comment-delete`, {
        community_token: token,
        community_member_id: member.community_member_id,
        member_token: member?.auth_token,
        comment_id: c.comment_id,
      });
      if (res.data?.error === 0) {
        setComments((cs) => cs.filter((x) => x.comment_id !== c.comment_id));
        setCount((n) => Math.max(0, n - 1));
        toast.success("Comment deleted.");
      } else {
        toast.error(res.data?.msg || "Could not delete the comment.");
      }
    } catch {
      toast.error("Could not delete the comment.");
    } finally {
      setDeletingCmt(false);
      setConfirmDeleteCmt(null);
    }
  };

  return (
    <>
    <div
      className={`border transition duration-200 ${POST_SHAPE[template] || POST_SHAPE.classic} ${
        post.pinned
          ? "bg-amber-50/50 border-amber-200 hover:border-amber-300"
          : "bg-white border-gray-200 hover:border-gray-300"
      }`}
    >
      {/* Author */}
      <div className="flex items-center gap-3">
        <span className="shrink-0">
          <Avatar name={post.author} img={post.author_img} primary={primary} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 text-[15px] leading-tight flex items-center gap-1.5 min-w-0">
            <span className="truncate">{post.author}</span>
            {post.author_is_admin && (
              <span
                className="shrink-0 text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: `${primary}1a`, color: primary }}
              >
                {post.author_role_name || "Admin"}
              </span>
            )}
            {post.author_specialist && (
              <span
                className="shrink-0 text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded-full"
                style={{ border: `1px solid ${primary}59`, color: primary }}
              >
                {post.author_specialist}
              </span>
            )}
          </p>
          <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1.5 min-w-0">
            <span className="shrink-0">{post.time_ago}</span>
            {post.category_name && (
              <span
                className="inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold"
                style={{ backgroundColor: `${primary}14`, color: primary }}
              >
                {post.category_emoji && <span aria-hidden="true">{post.category_emoji}</span>}
                {post.category_name}
              </span>
            )}
          </p>
        </div>
        <div className="ml-auto shrink-0 flex items-center gap-2">
          {post.pinned ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14.4 3.6l6 6a1 1 0 01-.7 1.7c-1.3.1-2.6.6-3.5 1.5l-.9.9 1.6 4.8a1 1 0 01-1.6 1.1l-3.4-3.4-4.2 4.2a1 1 0 01-1.4-1.4l4.2-4.2-3.4-3.4a1 1 0 011.1-1.6l4.8 1.6.9-.9c.9-.9 1.4-2.2 1.5-3.5a1 1 0 011.7-.7z" />
              </svg>
              Pinned
            </span>
          ) : null}
          {canManage && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="Post options"
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="1.6" />
                  <circle cx="12" cy="12" r="1.6" />
                  <circle cx="12" cy="19" r="1.6" />
                </svg>
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-1 w-40 bg-white border rounded-xl shadow-lg z-20 py-1.5">
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      startEdit();
                    }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="w-[16px] h-[16px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.4-9.4a2 2 0 112.8 2.8L11.8 15.2 8 16l.8-3.8 8.8-8.8z" />
                    </svg>
                    Edit post
                  </button>
                  {isFullAdmin && (
                    <button
                      onClick={pinPost}
                      className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <svg className="w-[16px] h-[16px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 4h6l-1 7 3 3v2H7v-2l3-3-1-7zM12 16v4" />
                      </svg>
                      {post.pinned ? "Unpin post" : "Pin post"}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirmDelete(true);
                    }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <svg className="w-[16px] h-[16px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-7 0v12a1 1 0 001 1h6a1 1 0 001-1V7" />
                    </svg>
                    Delete post
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {editing ? (
        <div className="mt-3">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={3}
            placeholder="Write something…"
            className={INPUT_CLS + " resize-none"}
            {...themeFocus(primary)}
          />

          {/* Image edit (image posts only) */}
          {isImagePost && (
            <div className="mt-2">
              {newImagePreview || (image && !removeImage) ? (
                <div className="relative inline-block">
                  <img
                    src={newImagePreview || image}
                    alt="preview"
                    className="max-h-48 rounded-xl border"
                  />
                  <button
                    onClick={() => {
                      setNewImage(null);
                      setNewImagePreview("");
                      setRemoveImage(true);
                    }}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-900 text-white text-sm flex items-center justify-center shadow"
                    aria-label="Remove image"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <p className="text-xs text-gray-400">Image removed.</p>
              )}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => editFileRef.current?.click()}
                  className="text-sm font-medium"
                  style={{ color: primary }}
                >
                  {image && !removeImage ? "Replace photo" : "Add photo"}
                </button>
                <input
                  ref={editFileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => pickEditImage(e.target.files?.[0])}
                />
              </div>
            </div>
          )}

          {/* Video edit */}
          {isVideoPost && (
            <div className="mt-2">
              <input
                value={editVideo}
                onChange={(e) => setEditVideo(e.target.value)}
                placeholder="Paste a YouTube, Vimeo, Wistia or MP4 link…"
                className={INPUT_CLS}
                {...themeFocus(primary)}
              />
              {editVideo.trim() && <VideoEmbed url={editVideo.trim()} />}
            </div>
          )}

          {/* Poll edit */}
          {isPollPost && (
            <div className="mt-2 space-y-2.5">
              {editPollOptions.map((opt, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <span
                    className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ backgroundColor: `${primary}14`, color: primary }}
                  >
                    {i + 1}
                  </span>
                  <input
                    value={opt}
                    onChange={(e) =>
                      setEditPollOptions((o) => o.map((v, idx) => (idx === i ? e.target.value : v)))
                    }
                    placeholder={`Option ${i + 1}`}
                    className={INPUT_CLS + " flex-1"}
                    {...themeFocus(primary)}
                  />
                  {editPollOptions.length > 2 && (
                    <button
                      type="button"
                      onClick={() => setEditPollOptions((o) => o.filter((_, idx) => idx !== i))}
                      className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
                      aria-label="Remove option"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              {editPollOptions.length < 6 && (
                <button
                  type="button"
                  onClick={() => setEditPollOptions((o) => [...o, ""])}
                  className="flex items-center gap-2 text-sm font-semibold pl-1 hover:opacity-80 transition"
                  style={{ color: primary }}
                >
                  <span className="w-7 h-7 rounded-full border-2 border-dashed flex items-center justify-center" style={{ borderColor: `${primary}66` }}>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                    </svg>
                  </span>
                  Add option
                </button>
              )}
              <p className="text-[11px] text-gray-400">Editing options resets existing votes.</p>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => setEditing(false)}
              disabled={savingEdit}
              className="text-sm font-medium text-gray-500 px-3 py-1.5 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={saveEdit}
              disabled={savingEdit}
              style={{ backgroundColor: primary }}
              className="text-white text-sm font-semibold px-4 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {savingEdit ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Image(s) first, then the text content below it. */}
          {images.length > 0 && <PostGallery images={images} />}
          {(() => {
            // Hide the embedded video URL from the text (it's shown as a player below).
            const shown = video ? (content || "").split(video).join("").trim() : content;
            return shown ? (
              <ReadMoreText
                text={shown}
                primary={primary}
                className="text-[15px] text-gray-800 mt-3 whitespace-pre-wrap break-words"
              />
            ) : null;
          })()}
          {!video && !image && extractLinkUrl(content) && (
            <LinkPreview url={extractLinkUrl(content)} />
          )}
        </>
      )}
      {!editing && video && <VideoEmbed url={video} />}
      {!editing && audio && (
        <div className="mt-3 flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <span
            className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-white"
            style={{ backgroundColor: primary }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 18V5l12-2v13" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
          </span>
          <audio controls src={audio} className="h-9 flex-1 min-w-0" />
        </div>
      )}
      {!editing && poll && (
        <Poll
          key={poll.options.map((o) => o.text).join("|")}
          post={{ ...post, poll }}
          member={member}
          token={token}
          primary={primary}
          onRequireAuth={onRequireAuth}
        />
      )}

      {/* Actions */}
      <div className="flex items-center gap-1.5 mt-4 pt-3 border-t border-gray-100 text-sm">
        <button
          onClick={like}
          style={liked ? { color: primary, backgroundColor: `${primary}14` } : undefined}
          className={`flex items-center gap-1.5 font-semibold px-3.5 py-2 rounded-full transition active:scale-95 ${
            liked ? "" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          } disabled:opacity-50`}
        >
          <svg className="w-[18px] h-[18px]" fill={liked ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 11v9H4a1 1 0 01-1-1v-7a1 1 0 011-1h3zm0 0l4.5-7.5A2 2 0 0115 4.6V9h4.2a2 2 0 011.97 2.34l-1.2 7A2 2 0 0118 20H7" />
          </svg>
          {likes > 0 ? likes : "Like"}
        </button>
        <button onClick={openComments} className="flex items-center gap-1.5 font-semibold text-gray-500 px-3.5 py-2 rounded-full hover:bg-gray-100 hover:text-gray-700 transition active:scale-95">
          <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h8m-8-4h8m-5 8H7l-3 3V6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2h-6z" />
          </svg>
          {count > 0 ? `${count} ${count === 1 ? "Comment" : "Comments"}` : "Comment"}
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
          {loadingComments ? (
            <p className="text-sm text-gray-400">Loading comments…</p>
          ) : (
            (() => {
              const all = comments || [];
              const repliesOf = (id) => all.filter((x) => x.parent_id === id);
              const renderComment = (c, isReply) => (
                <div key={c.comment_id} className="flex items-start gap-2.5 group">
                  <Avatar name={c.author} img={c.author_img} primary={primary} size={isReply ? 28 : 32} />
                  <div className="flex-1 min-w-0">
                    <div className="bg-gray-50 rounded-2xl px-3.5 py-2">
                      <div className="flex items-baseline gap-2">
                        <span className="font-semibold text-sm text-gray-900">{c.author}</span>
                        <span className="text-[11px] text-gray-400">{c.time_ago}</span>
                      </div>
                      {c.cmt && (
                        <ReadMoreText
                          text={c.cmt}
                          primary={primary}
                          limit={180}
                          className="text-sm text-gray-700 break-words whitespace-pre-wrap"
                        />
                      )}
                    </div>
                    {(() => {
                      const imgs = Array.isArray(c.images) && c.images.length ? c.images : (c.img ? [c.img] : []);
                      return imgs.length ? <PostGallery images={imgs} compact /> : null;
                    })()}
                    <div className="flex items-center gap-3 mt-1 ml-1.5 text-xs">
                      <button
                        onClick={() => likeComment(c)}
                        className={`inline-flex items-center gap-1 font-medium transition ${
                          c.liked ? "text-red-500" : "text-gray-400 hover:text-gray-600"
                        }`}
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={c.liked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                        </svg>
                        {c.liked ? "Liked" : "Like"}
                        {c.tot_likes > 0 ? ` · ${c.tot_likes}` : ""}
                      </button>
                      {member && !replyLocked && (
                        <button
                          onClick={() => {
                            // Replies attach to the thread's top-level parent; replying
                            // to a reply pre-fills an @mention of its author.
                            const threadId = isReply ? c.parent_id : c.comment_id;
                            clearReplyImages();
                            if (!isReply && replyTo === threadId) {
                              setReplyTo(null);
                            } else {
                              setReplyTo(threadId);
                              setReplyText(isReply ? `@${c.author} ` : "");
                            }
                          }}
                          className="font-medium text-gray-400 hover:text-gray-600"
                        >
                          Reply
                        </button>
                      )}
                      {member && (c.member_id === member.community_member_id || isAdmin) && (
                        <button
                          onClick={() => setConfirmDeleteCmt(c)}
                          className="font-medium text-gray-400 hover:text-red-500"
                        >
                          Delete
                        </button>
                      )}
                    </div>

                    {/* Replies + reply box (top-level comments only) */}
                    {!isReply && (repliesOf(c.comment_id).length > 0 || replyTo === c.comment_id) && (
                      <div className="mt-2.5 space-y-2.5 border-l-2 pl-3" style={{ borderColor: `${primary}1f` }}>
                        {(() => {
                          const reps = repliesOf(c.comment_id);
                          const expanded = expandedReplies[c.comment_id];
                          const visible = expanded ? reps : reps.slice(0, 2);
                          return (
                            <>
                              {visible.map((r) => renderComment(r, true))}
                              {reps.length > 2 && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedReplies((s) => ({ ...s, [c.comment_id]: !expanded }))}
                                  className="inline-flex items-center gap-1 text-xs font-semibold"
                                  style={{ color: primary }}
                                >
                                  <svg className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                                  </svg>
                                  {expanded ? "Show less" : `Show ${reps.length - 2} more ${reps.length - 2 === 1 ? "reply" : "replies"}`}
                                </button>
                              )}
                            </>
                          );
                        })()}
                        {replyTo === c.comment_id && member && !replyLocked && (
                          <div>
                            {replyImagePreviews.length > 0 && (
                              <div className={`relative mb-2 ml-9 ${replySending ? "min-h-[110px]" : ""}`}>
                                <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                                  {replyImagePreviews.map((src, i) => (
                                    <div key={i} className={`relative aspect-square transition ${replySending ? "opacity-50" : ""}`}>
                                      <img src={src} alt={`reply photo ${i + 1}`} className="h-full w-full rounded-lg border border-gray-200 object-cover" />
                                      {!replySending && (
                                        <button
                                          type="button"
                                          onClick={() => removeReplyImageAt(i)}
                                          className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-gray-900 text-xs text-white shadow"
                                          aria-label={`Remove photo ${i + 1}`}
                                        >
                                          ×
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {replySending && (
                                  <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/45 backdrop-blur-[1px]">
                                    <div className="flex flex-col items-center gap-2 rounded-2xl bg-white/95 px-5 py-4 shadow-lg" style={{ color: primary }}>
                                      <Spinner size={34} />
                                      <span className="text-base font-semibold tabular-nums">{replyUploadPct}%</span>
                                      <span className="text-xs font-medium text-gray-500">
                                        {replyUploadPct >= 100 ? "Processing…" : "Uploading photos…"}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            {replyGif && (
                              <div className="relative mb-2 ml-9 inline-block">
                                <img src={replyGif} alt="gif" className="max-h-40 rounded-xl border border-gray-200" />
                                <button
                                  type="button"
                                  onClick={() => setReplyGif("")}
                                  className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 text-sm text-white shadow"
                                  aria-label="Remove gif"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Avatar name={member.name} img={member.img} primary={primary} size={26} />
                              <MentionTextarea
                                autoFocus
                                value={replyText}
                                onChange={setReplyText}
                                onAddMention={(m) => setReplyMentions((list) => [...list, m])}
                                members={roster}
                                limit={tagLimit}
                                tagged={replyMentions.length}
                                primary={primary}
                                rows={1}
                                wrapperClassName="relative flex-1 min-w-0"
                                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitReply(c.comment_id); } }}
                                placeholder={`Reply to ${c.author}… use @ to tag`}
                                className="w-full resize-none rounded-2xl bg-gray-50 border border-gray-200 px-3.5 py-1.5 text-sm focus:outline-none focus:bg-white"
                              />
                              <input
                                ref={replyFileRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                multiple
                                className="hidden"
                                onChange={(e) => pickReplyImages(e.target.files)}
                              />
                              <button
                                type="button"
                                onClick={() => replyFileRef.current?.click()}
                                aria-label="Attach photos"
                                className="shrink-0 text-gray-500 hover:text-gray-700 transition"
                              >
                                <svg className="w-[17px] h-[17px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.5-4.5a2 2 0 012.8 0L16 16m-2-2l1.5-1.5a2 2 0 012.8 0L20 14M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm5 2a1 1 0 11-2 0 1 1 0 012 0z" />
                                </svg>
                              </button>
                              <GifPicker onPick={pickReplyGif} className="shrink-0" />
                              <EmojiPicker onPick={(em) => setReplyText((t) => t + em)} className="shrink-0" />
                              <button
                                type="button"
                                onClick={() => submitReply(c.comment_id)}
                                disabled={replySending || (!replyText.trim() && !replyImages.length && !replyGif)}
                                className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold disabled:opacity-40"
                                style={{ color: primary }}
                              >
                                {replySending ? <Spinner size={16} /> : "Reply"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
              return all.filter((c) => !c.parent_id).map((c) => renderComment(c, false));
            })()
          )}

          {member && replyLocked ? (
            <div className="mt-2 flex items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
              <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 0h10.5a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5H6.75a1.5 1.5 0 01-1.5-1.5v-6a1.5 1.5 0 011.5-1.5z" />
              </svg>
              <span>
                {replyLevelName
                  ? `Reach the “${replyLevelName}” level to reply to posts.`
                  : "Reach the required level to reply to posts."}
              </span>
            </div>
          ) : member ? (
            <form onSubmit={addComment} className="pt-1">
              {commentImagePreviews.length > 0 && (
                <div className="mb-2 ml-10 grid grid-cols-4 gap-2 sm:grid-cols-6">
                  {commentImagePreviews.map((src, i) => (
                    <div key={i} className="relative aspect-square">
                      <img src={src} alt={`preview ${i + 1}`} className="h-full w-full rounded-lg border border-gray-200 object-cover" />
                      <button
                        type="button"
                        onClick={() => removeCommentImageAt(i)}
                        className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-gray-900 text-xs text-white shadow"
                        aria-label={`Remove photo ${i + 1}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {commentGif && (
                <div className="relative mb-2 ml-10 inline-block">
                  <img src={commentGif} alt="gif" className="max-h-40 rounded-xl border border-gray-200" />
                  <button
                    type="button"
                    onClick={() => setCommentGif("")}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-900 text-white text-sm flex items-center justify-center shadow"
                    aria-label="Remove gif"
                  >
                    ×
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 sm:gap-2.5">
                <span className="shrink-0">
                  <Avatar name={member.name} img={member.img} primary={primary} size={32} />
                </span>
                <MentionTextarea
                  value={commentText}
                  onChange={setCommentText}
                  onAddMention={(m) => setCommentMentions((list) => [...list, m])}
                  members={roster}
                  limit={tagLimit}
                  tagged={commentMentions.length}
                  primary={primary}
                  rows={1}
                  wrapperClassName="relative flex-1 min-w-0"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(e); } }}
                  placeholder="Write a comment… use @ to tag"
                  className="w-full resize-none text-sm bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2.5 outline-none transition focus:bg-white placeholder:text-gray-400"
                />
                <input
                  ref={commentFileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  multiple
                  className="hidden"
                  onChange={(e) => pickCommentImages(e.target.files)}
                />
                <button
                  type="button"
                  onClick={() => commentFileRef.current?.click()}
                  aria-label="Attach photos"
                  className="shrink-0 text-gray-500 hover:text-gray-700 transition"
                >
                  <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.5-4.5a2 2 0 012.8 0L16 16m-2-2l1.5-1.5a2 2 0 012.8 0L20 14M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm5 2a1 1 0 11-2 0 1 1 0 012 0z" />
                  </svg>
                </button>
                <GifPicker onPick={pickCommentGif} className="shrink-0" />
                <EmojiPicker onPick={(e) => setCommentText((t) => t + e)} className="shrink-0" />
                <button
                  type="submit"
                  disabled={sending || (!commentText.trim() && !commentImages.length && !commentGif)}
                  style={{ backgroundColor: primary }}
                  className="shrink-0 flex items-center justify-center w-9 h-9 rounded-full text-white shadow-sm transition hover:opacity-90 disabled:opacity-40"
                  aria-label="Send comment"
                >
                  {sending ? (
                    <Spinner size={16} />
                  ) : (
                    <svg className="w-[18px] h-[18px] translate-x-[1px]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
                    </svg>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => onRequireAuth?.()}
              className="pt-1 text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              <span style={{ color: primary }} className="font-semibold">Sign in</span> to join the conversation.
            </button>
          )}
        </div>
      )}
    </div>

    {/* Delete-post confirmation modal */}
    {confirmDelete && (
      <ConfirmModal
        title="Delete post?"
        message="This post will be permanently removed. This action can't be undone."
        confirmLabel="Delete"
        busy={deleting}
        onCancel={() => !deleting && setConfirmDelete(false)}
        onConfirm={deletePost}
      />
    )}

    {/* Delete-comment confirmation modal */}
    {confirmDeleteCmt && (
      <ConfirmModal
        title="Delete comment?"
        message="This comment will be permanently removed."
        confirmLabel="Delete"
        busy={deletingCmt}
        onCancel={() => !deletingCmt && setConfirmDeleteCmt(null)}
        onConfirm={deleteComment}
      />
    )}
    </>
  );
}

/* Reusable destructive-action confirmation modal (replaces window.confirm). */
function ConfirmModal({ title, message, confirmLabel = "Confirm", busy, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
      onClick={onCancel}
    >
      <div
        className="bg-white w-full max-w-sm rounded-2xl shadow-xl p-6 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center">
          <svg className="w-7 h-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-7 0v12a1 1 0 001 1h6a1 1 0 001-1V7" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mt-4">{title}</h3>
        <p className="text-sm text-gray-500 mt-1.5">{message}</p>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex-1 border border-gray-200 text-gray-700 text-sm font-semibold py-2.5 rounded-xl hover:bg-gray-50 transition disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-red-700 transition disabled:opacity-60"
          >
            {busy && (
              <Spinner size={16} />
            )}
            {busy ? "Deleting…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CommunityFeed;
